import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

// Language codes
export type LanguageCode = 'en' | 'ja' | 'th' | 'vi';

// Language configuration
export const languages = {
  en: {
    code: 'en' as const,
    name: 'English',
    flag: '🇺🇸',
    nativeName: 'English'
  },
  ja: {
    code: 'ja' as const,
    name: 'Japanese',
    flag: '🇯🇵',
    nativeName: '日本語'
  },
  th: {
    code: 'th' as const,
    name: 'Thai',
    flag: '🇹🇭',
    nativeName: 'ไทย'
  },
  vi: {
    code: 'vi' as const,
    name: 'Vietnamese',
    flag: '🇻🇳',
    nativeName: 'Tiếng Việt'
  }
};

// Translation structure - add translations as needed
export const translations = {
  // Navigation
  nav: {
    home: {
      en: 'Home',
      ja: 'ホーム',
      th: 'หน้าแรก',
      vi: 'Trang chủ'
    },
    services: {
      en: 'Services',
      ja: 'サービス',
      th: 'บริการ',
      vi: 'Dịch vụ'
    },
    socialMediaMarketing: {
      en: 'Chinese Social Media Marketing',
      ja: 'マーケティングソリューション',
      th: 'การตลาดโซเชียลมีเดียสำหรับผู้พูดภาษาจีน',
      vi: "Marketing trên nền tảng mạng xã hội Trung Quốc"
    },
    searchAdsMediaOutreach: {
      en: 'Chinese Search Ads & Media Outreach',
      ja: '中華圏市場向け検索広告・メディアPR',
      th: 'โฆษณาค้นหาและการเข้าถึงสื่อสำหรับผู้พูดภาษาจีน',
      vi: "Quảng cáo trên công cụ tìm kiếm và truyền thông của Trung Quốc"
    },
    marketInsights: {
      en: 'Market Insights',
      ja: '市場インサイト',
      th: 'ข้อมูลเชิงลึกตลาด',
      vi: "Góc nhìn thị trường"
    },
    newsMarketInsights: {
      en: 'News & Market Insights',
      ja: 'ニュース・市場インサイト',
      th: 'ข่าวสารและข้อมูลเชิงลึกตลาด',
      vi: 'Tin tức & Phân tích thị trường'
    },
    aboutUs: {
      en: 'About Us',
      ja: '私たちについて',
      th: 'เกี่ยวกับเรา',
      vi: 'Về chúng tôi'
    },
    contactUs: {
      en: 'Contact Us',
      ja: 'お問い合わせ',
      th: 'ติดต่อเรา',
      vi: 'Liên hệ với chúng tôi'
    },
    language: {
      en: 'Language',
      ja: '言語',
      th: 'ภาษา',
      vi: 'Ngôn ngữ'
    }
  },
  // Homepage content
  home: {
    heroTitle: {
      en: '',
      ja: '',
      th: '',
      vi: ''
    },
    heroHighlight: {
      en: 'Connecting Your Business with 150 Million Chinese-Speaking Tourists',
      ja: 'AIドリブンのテクノロジーマーケティングソリューションで、グローバルビジネスを支援します。',
      th: 'เชื่อมธุรกิจของคุณกับนักท่องเที่ยวจีน 150 ล้านคน',
      vi: 'Kết nối doanh nghiệp của bạn với 150 triệu du khách nói tiếng Trung'
    },
    heroSubtitle: {
      en: 'Chinese-speaking market experts growing your business with powerful online to offline solutions.',
      ja: '',
      th: 'ผู้เชี่ยวชาญตลาดจีนจะช่วยเติบโตธุรกิจของคุณด้วยโซลูชั่นที่มีประสิทธิภาพออนไลน์สู่ออฟไลน์',
      vi: 'Chuyên gia về thị trường khách hàng nói tiếng Trung giúp phát triển doanh nghiệp của bạn với các giải pháp mạnh mẽ từ trực tuyến đến ngoại tuyến.'
    },
    ourServices: {
      en: 'Our Services',
      ja: 'サービス',
      th: 'บริการของเรา',
      vi: 'Dịch vụ của chúng tôi'
    },
    ourServicesDescription: {
      en: 'We provide integrated marketing solutions designed for the Chinese-speaking market, combining digital expertise with in-market insights to drive real business results.',
      ja: '3つのマーケティングソリューションを通じ、「買う理由」と「買う行動」をデザインし、継続的な売上成長につなげます。',
      th: 'เราให้บริการโซลูชั่นการตลาดแบบบูรณาการที่ออกแบบสำหรับตลาดจีน โดยผสมผสานความเชี่ยวชาญด้านดิจิทัลกับข้อมูลเชิงลึกในตลาด เพื่อขับเคลื่อนผลลัพธ์ทางธุรกิจที่แท้จริง',
      vi: "Chúng tôi cung cấp các giải pháp marketing tích hợp dành riêng cho thị trường nói tiếng Trung, kết hợp chuyên môn về các nền tảng kỹ thuật số với hiểu biết thị trường thực tế để mang lại kết quả kinh doanh rõ rệt."
    },
    // Service box titles and descriptions
    serviceBoxSocialMediaTitle: {
      en: 'Chinese Social Media Marketing & Online-to-Offline Services',
      ja: '中華圏SNSマーケティング・O2Oマーケティング',
      th: 'การตลาดสื่อสังคมจีนและบริการออนไลน์สู่ออฟไลน์',
      vi: "Marketing trên mạng xã hội Trung Quốc & Dịch vụ Trực tuyến đến Ngoại tuyến"
    },
    serviceBoxSocialMediaDesc: {
      en: 'Professional social media marketing that turns online engagement into offline sales',
      ja: 'オンラインの関心をオフラインの成果へ導く、プロフェッショナルSNSマーケティング',
      th: 'การตลาดสื่อสังคมแบบมืออาชีพที่เปลี่ยนการมีส่วนร่วมออนไลน์ให้เป็นยอดขายออฟไลน์',
      vi: "Dịch vụ marketing trên mạng xã hội chuyên nghiệp giúp chuyển đổi tương tác trực tuyến thành doanh số ngoại tuyến"
    },
    serviceBoxKolTitle: {
      en: 'Chinese Influencer (KOL/KOC) Marketing',
      ja: '中華圏インフルエンサー（KOL/KOC）マーケティング',
      th: 'การตลาดอินฟลูเอนเซอร์จีน (KOL/KOC)',
      vi: "Marketing Influencer Trung Quốc (KOL/KOC)"
    },
    serviceBoxKolDesc: {
      en: 'Authentic influencer collaborations, boosting the visibility of your business, increasing trust and purchase intent',
      ja: '弊社独自の中華圏インフルエンサーネットワークを活かした戦略的コラボレーションで、認知・信頼・購買意欲を同時に高めます。',
      th: 'การร่วมงานกับอินฟลูเอนเซอร์ที่แท้จริง เพิ่มความเป็นที่รู้จักของธุรกิจ เพิ่มความไว้วางใจและความตั้งใจซื้อ',
      vi: "Hợp tác với các influencer uy tín, nâng cao mức độ nhận diện thương hiệu, tăng niềm tin và thúc đẩy ý định mua hàng"
    },
    serviceBoxBaiduTitle: {
      en: 'Chinese Search Engine (Baidu) Ads & Media Outreach',
      ja: '中国検索エンジン（Baidu）広告・メディアPR',
      th: 'โฆษณาเซิร์ชเอนจินจีน (Baidu) และการเข้าถึงสื่อ',
      vi: "Quảng cáo trên công cụ tìm kiếm Trung Quốc (Baidu) & Truyền thông thương hiệu"
    },
    serviceBoxBaiduDesc: {
      en: 'Baidu advertising and media coverage to increase visibility and brand authority',
      ja: 'Baidu広告と中華圏メディアPRで可視性とブランドの信頼性を向上',
      th: 'โฆษณา Baidu และการครอบคลุมสื่อเพื่อเพิ่มความเป็นที่รู้จักและสร้างความน่าเชื่อถือของแบรนด์',
      vi: "Quảng cáo trên Baidu và chiến dịch truyền thông giúp gia tăng độ phủ và uy tín thương hiệu"
    },
    serviceBoxMoreTitle: {
      en: 'More Services Coming Soon',
      ja: 'Coming Soon – まもなく、新サービスをお届けします',
      th: 'บริการเพิ่มเติมเร็วๆ นี้',
      vi: "Nhiều dịch vụ khác sắp ra mắt"
    },
    serviceBoxMoreDesc: {
      en: '',
      ja: '',
      th: '',
      vi: ''
    },
    brandDisclaimer: {
      en: 'Logos shown above represent brands we have supported directly or indirectly through partners, distributors, or retailers.',
      ja: '掲載されているロゴには、弊社がパートナー、代理店、小売業者を通じてサポートしたブランドが含まれます。',
      th: 'โลโก้ที่แสดงข้างต้นแทนแบรนด์ที่เราได้ให้การสนับสนุนโดยตรงหรือโดยอ้อมผ่านพาร์ทเนอร์ ตัวแทนจำหน่าย หรือผู้ขายปลีก',
      vi: 'Các logo hiển thị ở trên đại diện cho các thương hiệu mà chúng tôi đã hỗ trợ trực tiếp hoặc gián tiếp thông qua các đối tác, nhà phân phối hoặc cửa hàng bán lẻ.'
    },
    // Why Choose Section
    whyChooseTitle: {
      en: 'Why Choose Z Creative Partners?',
      ja: 'Z Creative Partnersが選ばれる理由',
      th: 'ทำไมต้องเลือก Z Creative Partners?',
      vi: 'Tại sao chọn Z Creative Partners?'
    },
    whyChooseSubtitle: {
      en: 'Proven results, exceptional service, and deep market expertise',
      ja: '確かな実績、高品質なサービス、深い市場洞察',
      th: 'ผลลัพธ์ที่พิสูจน์แล้ว บริการที่ยอดเยี่ยม และความเชี่ยวชาญด้านตลาดอย่างลึกซึ้ง',
      vi: "Hiệu quả được khẳng định, dịch vụ xuất sắc và am hiểu chuyên sâu về thị trường"
    },
    // Metrics
    contractRenewalRate: {
      en: 'Contract Renewal Rate',
      ja: '契約更新率',
      th: 'อัตราการต่อสัญญา',
      vi: 'Tỷ lệ gia hạn hợp đồng'
    },
    clientSatisfactionRate: {
      en: 'Client Satisfaction Rate',
      ja: '顧客満足度',
      th: 'อัตราความพึงพอใจของลูกค้า',
      vi: 'Tỷ lệ hài lòng của khách hàng'
    },
    referralRate: {
      en: 'Referral Rate',
      ja: '紹介率',
      th: 'อัตราการแนะนำ',
      vi: 'Tỷ lệ giới thiệu'
    },
    referralRateSub: {
      en: '(Via existing clients)',
      ja: '（既存顧客経由）',
      th: '(ผ่านลูกค้าเดิม)',
      vi: '(Thông qua khách hàng hiện tại)'
    },
    brandsSupported: {
      en: 'Brands Supported',
      ja: 'ご支援ブランド数',
      th: 'แบรนด์ที่สนับสนุน',
      vi: "Thương hiệu đã hợp tác"
    },
    industriesSupported: {
      en: 'Industries Supported',
      ja: 'ご支援業界数',
      th: 'อุตสาหกรรมที่สนับสนุน',
      vi: "Lĩnh vực đã hỗ trợ"
    },
    snsPostsCreated: {
      en: 'Social Media Posts Created',
      ja: 'SNS投稿作成数',
      th: 'โพสต์ SNS ที่สร้าง',
      vi: 'Số bài viết SNS đã được tạo'
    },
    snsPostsCreatedSub: {
      en: '(100% original content)',
      ja: '（100％オリジナルコンテンツ）',
      th: '(100% เนื้อหาต้นฉบับ)',
      vi: '(100% nội dung gốc)'
    },
    contentOwnership: {
      en: 'Content Ownership',
      ja: 'コンテンツ所有権',
      th: 'กรรมสิทธิ์เนื้อหา',
      vi: 'Quyền sở hữu nội dung'
    },
    contentOwnershipSub: {
      en: 'Belongs to the client',
      ja: 'クライアント様所有',
      th: 'เป็นของลูกค้า',
      vi: 'Thuộc về khách hàng'
    },
    alignedWithTrends: {
      en: 'Aligned with Trends',
      ja: '市場トレンドに対応',
      th: 'สอดคล้องกับเทรนด์',
      vi: 'Phù hợp với xu hướng'
    },
    alignedWithTrendsSub: {
      en: 'In the Chinese-speaking market',
      ja: '中華圏市場において',
      th: 'ในตลาดจีน',
      vi: 'Trong thị trường nói tiếng Trung'
    },
    // Key Strengths
    ourKeyStrengths: {
      en: 'Our Key Strengths',
      ja: '私たちの強み',
      th: 'จุดแข็งหลักของเรา',
      vi: "Thế mạnh của chúng tôi"
    },
    strengthGlobalTeamTitle: {
      en: 'Global Team for Local Success',
      ja: 'ローカル成功のためのグローバルチーム',
      th: 'ทีมสากลเพื่อความสำเร็จในท้องถิ่น',
      vi: "Đội ngũ quốc tế phát triển giá trị bản địa"
    },
    strengthGlobalTeamDesc: {
      en: 'Experts from Japan, Greater China, Thailand, and Vietnam bring deep market understanding',
      ja: '日本、中華圏、アセアンなどの国・地域から集まったメンバーが、現地市場のトレンド、消費者行動、文化的ニュアンスを深く理解',
      th: 'ผู้เชี่ยวชาญจากญี่ปุ่น จีนใหญ่ ไทย และเวียดนาม นำมาซึ่งความเข้าใจตลาดอย่างลึกซึ้ง',
      vi: "Các chuyên gia từ Nhật Bản, Trung Quốc, Thái Lan và Việt Nam mang lại sự hiểu biết thị trường sâu sắc"
    },
    strengthExpertTeamTitle: {
      en: 'Expert Team with Local Insights',
      ja: '現地の知見を活かした専門チーム',
      th: 'ทีมผู้เชี่ยวชาญที่มีข้อมูลเชิงลึกในท้องถิ่น',
      vi: "Đội ngũ chuyên gia am hiểu bản địa"
    },
    strengthExpertTeamDesc: {
      en: 'Highly skilled professionals with native language capabilities and cultural expertise',
      ja: '現地の言語や文化に精通した高いスキルを持つプロフェッショナルで構成',
      th: 'ผู้เชี่ยวชาญที่มีทักษะสูงพร้อมความสามารถทางภาษาแม่และความเชี่ยวชาญด้านวัฒนธรรม',
      vi: "Các chuyên gia giàu kinh nghiệm, thông thạo ngôn ngữ bản địa và có kiến thức sâu sắc về văn hóa"
    },
    strengthIndustryTitle: {
      en: 'Comprehensive Industry Experience',
      ja: 'グローバルな視野に立脚した包括的な業界経験',
      th: 'ประสบการณ์ในอุตสาหกรรมอย่างครบถ้วน',
      vi: 'Kinh nghiệm toàn diện trong ngành'
    },
    strengthIndustryDesc: {
      en: 'Proven track record across tourism, retail, food & beverage, beauty, and hospitality sectors',
      ja: '観光、小売、飲食、美容、ホスピタリティ業界での実績',
      th: 'ผลงานที่พิสูจน์แล้วในหลายภาคส่วน ได้แก่ การท่องเที่ยว ค้าปลีก อาหารและเครื่องดื่ม ความงาม และการบริการ',
      vi: "Thành tích đã được khẳng định trong các lĩnh vực du lịch, bán lẻ, thực phẩm & đồ uống, làm đẹp và dịch vụ khách sạn"
    },
    strengthTailoredTitle: {
      en: 'Tailored Strategies for Lasting Success',
      ja: '持続的な成功のためのオーダーメイド戦略',
      th: 'กลยุทธ์ที่ปรับให้เหมาะสมเพื่อความสำเร็จที่ยั่งยืน',
      vi: "Các chiến lược riêng biệt tạo hiệu quả lâu dài"
    },
    strengthTailoredDesc: {
      en: 'Customized marketing plans designed to achieve your specific business objectives',
      ja: 'クライアント様がそれぞれ固有のビジネス環境と目標を達成するためにカスタマイズしたマーケティング戦略を開発',
      th: 'แผนการตลาดที่ปรับแต่งตามความต้องการ เพื่อบรรลุวัตถุประสงค์ทางธุรกิจของคุณโดยเฉพาะ',
      vi: "Giải pháp marketing được tùy chỉnh, định hướng trực tiếp đến mục tiêu kinh doanh cụ thể của bạn"
    },
    ourValuedBrands: {
      en: 'Our Valued Brands',
      ja: '弊社ご支援ブランド(抜粋)',
      th: 'แบรนด์ที่มีค่าของเรา',
      vi: "Các thương hiệu đã đồng hành cùng chúng tôi"
    }
  },
  // Contact section
  contact: {
    contactUs: {
      en: 'Contact Us',
      ja: 'お問い合わせ',
      th: 'ติดต่อเรา',
      vi: 'Liên hệ với chúng tôi'
    },
    globalOffices: {
      en: 'Global Offices',
      ja: 'グローバルオフィス',
      th: 'สำนักงานทั่วโลก',
      vi: 'Các văn phòng toàn cầu'
    },
    japanOffice: {
      en: 'Japan Office',
      ja: '日本オフィス',
      th: 'สำนักงานญี่ปุ่น',
      vi: 'Văn phòng Nhật Bản'
    },
    japanAddress: {
      en: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo',
      ja: '東京都港区六本木三丁目16番12号六本木KSビル5F',
      th: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo',
      vi: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo'
    },
    thailandOffice: {
      en: 'Thailand Office',
      ja: 'タイオフィス',
      th: 'สำนักงานไทย',
      vi: 'Văn phòng Thái Lan'
    },
    thailandAddress: {
      en: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260',
      ja: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260',
      th: '71 อุดมสุข ซอย 46 บางนาเหนือ บางนา กรุงเทพฯ 10260',
      vi: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260'
    },
    vietnamOffice: {
      en: 'Vietnam Office',
      ja: 'ベトナムオフィス',
      th: 'สำนักงานเวียดนาม',
      vi: 'Văn phòng Việt Nam'
    },
    vietnamAddress: {
      en: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      ja: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      th: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      vi: 'Toà nhà Sabay, số 5, đường Đồng Nai, Thành phố Hồ Chí Minh'
    },
    chinaOffice: {
      en: 'China Office',
      ja: '中国オフィス',
      th: 'สำนักงานจีน',
      vi: 'Văn phòng Trung Quốc'
    },
    chinaAddress: {
      en: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      ja: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      th: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      vi: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province'
    },
    emailLabel: {
      en: 'Email',
      ja: 'メール',
      th: 'อีเมล',
      vi: 'Email'
    },
    subscribe: {
      en: 'Subscribe to Our',
      ja: '私たちの',
      th: 'สมัครรับ',
      vi: 'Đăng ký nhận'
    },
    newsletter: {
      en: 'Newsletter',
      ja: 'メールマガジンに登録',
      th: 'จดหมายข่าว',
      vi: 'Bản tin của chúng tôi'
    },
    newsletterDesc: {
      en: 'Get the latest news, market insights, and expert tips on reaching Chinese consumers delivered straight to your inbox.',
      ja: 'GEOや中華圏・アセアン市場関連の最新ニュース、市場インサイト、プロによる実践的なヒントをメールでお届けします。',
      th: 'รับข่าวสารล่าสุด ข้อมูลเชิงลึกตลาด และคำแนะนำจากผู้เชี่ยวชาญในการเข้าถึงผู้บริโภคจีน ส่งตรงถึงกล่องอีเมลของคุณ',
      vi: "Cập nhật tin tức mới nhất, phân tích thị trường và lời khuyên chuyên gia về cách tiếp cận người tiêu dùng Trung Quốc – gửi thẳng đến hộp thư của bạn."
    },
    enterYourEmail: {
      en: 'Enter your email address',
      ja: 'メールアドレスを入力',
      th: 'กรอกที่อยู่อีเมลของคุณ',
      vi: 'Nhập email của bạn'
    },
    subscribeButton: {
      en: 'Subscribe',
      ja: '登録する',
      th: 'สมัคร',
      vi: 'Đăng ký'
    },
    subscribing: {
      en: 'Subscribing...',
      ja: '登録中...',
      th: 'กำลังสมัคร...',
      vi: 'Đang đăng ký...'
    },
    name: {
      en: 'Name',
      ja: 'お名前',
      th: 'ชื่อ',
      vi: 'Họ tên'
    },
    email: {
      en: 'Email',
      ja: 'メールアドレス',
      th: 'อีเมล',
      vi: 'Email'
    },
    subject: {
      en: 'Subject',
      ja: '件名',
      th: 'หัวข้อ',
      vi: 'Chủ đề'
    },
    message: {
      en: 'Message',
      ja: 'メッセージ',
      th: 'ข้อความ',
      vi: 'Tin nhắn'
    },
    company: {
      en: 'Company',
      ja: '会社名',
      th: 'บริษัท',
      vi: 'Công ty'
    },
    phone: {
      en: 'Phone',
      ja: '電話番号',
      th: 'โทรศัพท์',
      vi: 'Điện thoại'
    },
    optional: {
      en: '(Optional)',
      ja: '（任意）',
      th: '(ไม่บังคับ)',
      vi: '(Tùy chọn)'
    },
    sendEmail: {
      en: 'Send Email',
      ja: '送信',
      th: 'ส่งอีเมล',
      vi: 'Gửi email'
    }
  },
  // Footer
  footer: {
    privacyPolicy: {
      en: 'Privacy Policy',
      ja: 'プライバシーポリシー',
      th: 'นโยบายความเป็นส่วนตัว',
      vi: 'Chính sách bảo mật'
    },
    termsOfUse: {
      en: 'Terms of Use',
      ja: '利用規約',
      th: 'ข้อกำหนดการใช้งาน',
      vi: 'Điều khoản sử dụng'
    },
    cookiePolicy: {
      en: 'Cookie Policy',
      ja: 'クッキーポリシー',
      th: 'นโยบายคุกกี้',
      vi: 'Chính sách cookie'
    },
    copyright: {
      en: 'Z Creative Partners Co., Ltd. © 2025. All rights reserved.',
      ja: 'Z Creative Partners Co., Ltd. © 2025. 無断転載・複製を禁じます。',
      th: 'Z Creative Partners Co., Ltd. © 2025. สงวนลิขสิทธิ์ทั้งหมด',
      vi: 'Z Creative Partners Co., Ltd. © 2025. Tất cả quyền được bảo lưu.'
    }
  },
  // Social Media Marketing page
  socialMedia: {
    heroTitle: {
      en: 'Chinese Social Media Marketing & O2O Solutions – Turning Online Engagement into Offline Sales',
      ja: '中華圏とアセアンの顧客に特化した、マーケティングソリューション',
      th: 'การตลาดสื่อสังคมจีนและโซลูชั่น O2O - เปลี่ยนการมีส่วนร่วมออนไลน์ให้เป็นยอดขายออฟไลน์',
      vi: 'Marketing mạng xã hội Trung Quốc & Giải pháp O2O - Chuyển đổi tương tác trực tuyến thành doanh số ngọại tuyến'
    },
    heroDescription: {
      en: 'We manage your presence on key Chinese language platforms including RedBook(Xiaohongshu), WeChat, Weibo, and Douyin, ensuring consistent engagement, brand growth, and conversion from online awareness to offline sales.',
      ja: '「買う理由」と「買う行動」をデザインし、継続的な売上成長につなげます',
      th: 'เราบริหารการปรากฏตัวของคุณบนแพลตฟอร์มภาษาจีนที่สำคัญ รวมถึง RedBook(Xiaohongshu), WeChat, Weibo และ Douyin เพื่อให้มั่นใจในการมีส่วนร่วมที่สม่ำเสมอ การเติบโตของแบรนด์ และการแปลงจากการรับรู้ออนไลน์เป็นยอดขายออฟไลน์',
      vi: 'Chúng tôi đồng hành cùng doanh nghiệp trong việc quản lý sự hiện diện thương hiệu trên các nền tảng Trung Quốc trọng yếu như Redbook(Xiaohongshu), WeChat, Weibo và Douyin, bảo đảm gắn kết bền vững với khách hàng, mở rộng thương hiệu và tối ưu chuyển đổi từ trực tuyến sang ngoại tuyến.'
    },
    whyThisMatters: {
      en: 'Why It Matters',
      ja: 'なぜ重要なのか',
      th: 'ทำไมจึงสำคัญ',
      vi: 'Tại sao điều này quan trọng'
    },
    whyMattersPoint1Title: {
      en: 'Primary Discovery Channels',
      ja: '主要な認知経路',
      th: 'ช่องทางค้นพบหลัก',
      vi: 'Kênh khám phá chính'
    },
    whyMattersPoint1Desc: {
      en: 'RedBook(Xiaohongshu), WeChat, Weibo, and Douyin are where Chinese customers discover and verify brands.',
      ja: 'Redbook(Xiaohongshu)、WeChat、Weibo、Douyinは、中華圏の顧客がブランドを知り、信頼性を確かめる場所です。',
      th: 'RedBook(Xiaohongshu), WeChat, Weibo และ Douyin เป็นที่ที่ลูกค้าจีนค้นพบและตรวจสอบแบรนด์',
      vi: 'RedBook(Xiaohongshu), WeChat, Weibo và Douyin là nơi khách hàng Trung Quốc khám phá và xác minh thương hiệu.'
    },
    whyMattersPoint2Title: {
      en: 'Consistent Management',
      ja: '一貫したブランド発信',
      th: 'การจัดการที่สม่ำเสมอ',
      vi: 'Quản lý nhất quán'
    },
    whyMattersPoint2Desc: {
      en: 'Success requires consistent, high-quality management that builds trust.',
      ja: '成功の鍵は、信頼を生む一貫性と高品質なプレゼンス管理にあります。',
      th: 'ความสำเร็จต้องการการจัดการที่สม่ำเสมอและมีคุณภาพสูงเพื่อสร้างความไว้วางใจ',
      vi: "Thành công chỉ đến khi có sự quản lý chặt chẽ, nhất quán và chất lượng, giúp nuôi dưỡng và củng cố niềm tin của khách hàng."
    },
    whyMattersPoint3Title: {
      en: 'Online to Offline',
      ja: 'オンラインからオフラインへ',
      th: 'ออนไลน์สู่ออฟไลน์',
      vi: "Từ trực tuyến đến ngoại tuyến"
    },
    whyMattersPoint3Desc: {
      en: 'Connect digital engagement with real-world sales and customer actions.',
      ja: 'デジタル上のエンゲージメントを、実際の顧客体験や行動へと導きます。',
      th: 'เชื่อมต่อการมีส่วนร่วมดิจิทัลกับยอดขายและการกระทำของลูกค้าในโลกจริง',
      vi: "Liên kết trải nghiệm trực tuyến với doanh thu thực tế và hành vi khách hàng."
    },
    whyMattersPoint4Title: {
      en: 'Complete Solution',
      ja: '包括的なソリューション',
      th: 'โซลูชั่นที่สมบูรณ์',
      vi: "Giải pháp toàn diện"
    },
    whyMattersPoint4Desc: {
      en: 'End-to-end management that keeps your brand relevant and persuasive.',
      ja: 'ブランドの魅力と信頼性を高めるために、エンドツーエンドでの一貫した運用をサポートします。',
      th: 'การจัดการแบบครบวงจรที่ทำให้แบรนด์ของคุณมีความเกี่ยวข้องและเป็นที่น่าเชื่อถือ',
      vi: "Giải pháp quản lý toàn diện, đảm bảo thương hiệu luôn giữ được sự hiện diện và sức thuyết phục."
    },
    whatWeOffer: {
      en: 'What We Offer',
      ja: '私たちのソリューション',
      th: 'สิ่งที่เราเสนอ',
      vi: 'Dịch vụ chúng tôi cung cấp'
    },
    offerPoint1Title: {
      en: 'Official Account Setup & Optimization',
      ja: '公式アカウント構築・最適化',
      th: 'การตั้งค่าและปรับปรุงบัญชีอย่างเป็นทางการ',
      vi: 'Thiết lập và tối ưu hóa tài khoản chính thức'
    },
    offerPoint1Desc: {
      en: 'Professional profile creation, brand-aligned visuals, keyword-rich descriptions.',
      ja: 'プロフェッショナルなプロフィール作成、ブランドに沿ったビジュアルデザイン、SEO最適化された説明文',
      th: 'การสร้างโปรไฟล์แบบมืออาชีพ ภาพที่สอดคล้องกับแบรนด์ คำอธิบายที่เต็มไปด้วยคีย์เวิร์ด',
      vi: 'Xây dựng hồ sơ chuyên nghiệp, thiết kế hình ảnh phù hợp thương hiệu, nội dung mô tả giàu từ khóa.'
    },
    offerPoint2Title: {
      en: 'Content Planning & Production',
      ja: 'コンテンツの企画と制作',
      th: 'การวางแผนและผลิตเนื้อหา',
      vi: 'Lập kế hoạch và sản xuất nội dung'
    },
    offerPoint2Desc: {
      en: 'Monthly content calendars, localized copywriting, professional visuals.',
      ja: '年間から月次までのコンテンツカレンダー、ローカライズされたコピー、プロフェッショナルなビジュアル',
      th: 'ปฏิทินเนื้อหารายเดือน การเขียนข้อความที่ปรับให้เข้ากับท้องถิ่น ภาพมืออาชีพ',
      vi: 'Xây dựng lịch đăng bài hàng tháng, sáng tạo nội dung phù hợp thị trường, thiết kế hình ảnh chuyên nghiệp.'
    },
    offerPoint3Title: {
      en: 'Publishing & Engagement',
      ja: '投稿・エンゲージメント',
      th: 'การเผยแพร่และการมีส่วนร่วม',
      vi: 'Xuất bản và tương tác'
    },
    offerPoint3Desc: {
      en: 'Regular posting, comment and message response, audience interaction.',
      ja: '定期的な投稿、コメントやメッセージの対応、オーディエンスとの関係構築',
      th: 'การโพสต์ตามปกติ การตอบสนองต่อความคิดเห็นและข้อความ การมีปฏิสัมพันธ์กับผู้ชม',
      vi: 'Đăng bài thường xuyên, phản hồi bình luận và tin nhắn, tương tác với người dùng.'
    },
    offerPoint4Title: {
      en: 'Online-to-Offline Integration',
      ja: 'オンラインとオフラインの統合',
      th: 'การผสานรวมออนไลน์สู่ออฟไลน์',
      vi: 'Tích hợp trực tuyến đến ngoại tuyến'
    },
    offerPoint4Desc: {
      en: 'Linking online promotions to in-store events, offers, and customer incentives.',
      ja: 'オンラインプロモーションを、店頭イベントや特典、顧客向けキャンペーンと連携',
      th: 'เชื่อมโยงโปรโมชั่นออนไลน์กับงานในร้าน ข้อเสนอ และแรงจูงใจของลูกค้า',
      vi: 'Liên kết hoạt động quảng bá trực tuyến với sự kiện tại điểm bán, ưu đãi và chương trình dành cho khách hàng.'
    },
    offerPoint5Title: {
      en: 'Performance Monitoring & Reporting',
      ja: 'パフォーマンス分析レポート',
      th: 'การติดตามและรายงานผลงาน',
      vi: 'Giám sát hiệu suất và báo cáo'
    },
    offerPoint5Desc: {
      en: 'Data tracking and monthly reports with actionable recommendations.',
      ja: 'データ計測と実行可能な改善提案を盛り込んだ月次レポートで、成果を可視化し、継続的な改善を支援',
      th: 'การติดตามข้อมูลและรายงานประจำเดือนพร้อมข้อแนะนำที่สามารถนำไปปฏิบัติได้',
      vi: 'Theo dõi dữ liệu và báo cáo hàng tháng với các khuyến nghị khả thi.'
    },
    onlineSocialMediaTitle: {
      en: 'Online: Social Media Marketing',
      ja: '事例紹介（SNS & O2O Growth Engine）',
      th: 'ออนไลน์: การตลาดสื่อสังคม',
      vi: 'Marketing trực tuyến:\nMarketing trên các nền tảng mạng xã hội'
    },
    industrySubtitle: {
      en: 'Targeted industry-specific campaigns across major Chinese social platforms',
      ja: '',
      th: 'แคมเปญเฉพาะอุตสาหกรรมที่ตรงเป้าหมายบนแพลตฟอร์มโซเชียลจีนหลัก',
      vi: 'Các chiến dịch cho từng ngành mục tiêu trên các nền tảng mạng xã hội lớn của Trung Quốc'
    },
    industryFoodTitle: {
      en: 'Food & Beverage',
      ja: '飲食業',
      th: 'อาหารและเครื่องดื่ม',
      vi: 'Thực phẩm & Đồ uống'
    },
    industryFoodDesc: {
      en: 'Make your restaurant a must-visit for Chinese-speaking diners through social buzz',
      ja: 'ソーシャルメディアでの話題づくりにより、中華圏のお客様にとって必訪のレストランに',
      th: 'ทำให้ร้านอาหารของคุณเป็นจุดหมายปลายทางสำหรับนักชิมที่พูดภาษาจีนผ่านการสร้างกระแสบนโซเชียล',
      vi: 'Biến nhà hàng của bạn thành điểm đến yêu thích của thực khách nói tiếng Trung, nơi họ tìm thấy những trải nghiệm ẩm thực hấp dẫn qua làn sóng chia sẻ sôi động trên mạng xã hội'
    },
    industryHotelsTitle: {
      en: 'Hotels & Tourism',
      ja: 'ホテル・観光',
      th: 'โรงแรมและการท่องเที่ยว',
      vi: 'Khách sạn & Du lịch'
    },
    industryHotelsDesc: {
      en: 'Connect with Chinese-speaking tourists through strategic social media and influencer outreach',
      ja: '戦略的なSNSとインフルエンサーアウトリーチで中華圏観光客とつながる',
      th: 'เชื่อมต่อกับนักท่องเที่ยวที่พูดภาษาจีนผ่านโซเชียลมีเดียเชิงกลยุทธ์และความร่วมมืออินฟลูเอนเซอร์',
      vi: 'Kết nối với du khách nói tiếng Trung bằng chiến lược truyền thông và hợp tác influencer hiệu quả'
    },
    industryRetailTitle: {
      en: 'Retail',
      ja: '小売業',
      th: 'ค้าปลีก',
      vi: 'Bán lẻ'
    },
    industryRetailDesc: {
      en: 'Boost Chinese-speaking customer engagement with O2O campaigns and social commerce',
      ja: 'O2Oキャンペーンとソーシャルコマースで中華圏顧客のエンゲージメントを向上',
      th: 'เพิ่มการมีส่วนร่วมของลูกค้าที่พูดภาษาจีนด้วยแคมเปญ O2O และโซเชียลคอมเมิร์ซ',
      vi: 'Tăng sự gắn kết của khách hàng nói tiếng Trung thông qua các chiến dịch O2O sáng tạo và hoạt động trên mạng xã hội'
    },
    industryBeautyTitle: {
      en: 'Beauty & Skincare',
      ja: '美容・スキンケア',
      th: 'ความงามและผิวพรรณ',
      vi: 'Làm đẹp & Chăm sóc da'
    },
    industryBeautyDesc: {
      en: 'Grow awareness and sales via social media beauty content and influencer partnerships',
      ja: 'SNS美容コンテンツとインフルエンサーパートナーシップで認知度と売上を拡大',
      th: 'เพิ่มการรับรู้และยอดขายผ่านคอนเทนต์ความงามบนโซเชียลและความร่วมมืออินฟลูเอนเซอร์',
      vi: 'Gia tăng độ nhận biết thương hiệu và doanh số bán hàng bằng nội dung làm đẹp hấp dẫn và hợp tác cùng các influencer có tầm ảnh hưởng trong cộng đồng người tiêu dùng Trung Quốc'
    },
    industryFashionTitle: {
      en: 'Fashion',
      ja: 'ファッション',
      th: 'แฟชั่น',
      vi: 'Thời trang'
    },
    industryFashionDesc: {
      en: 'Connect with Chinese fashion enthusiasts via trendsetting content and KOL campaigns',
      ja: 'トレンドセッティングなコンテンツとKOLキャンペーンで中華圏ファッション愛好家とつながる',
      th: 'เชื่อมต่อกับผู้ชื่นชอบแฟชั่นจีนผ่านคอนเทนต์กำหนดเทรนด์และแคมเปญ KOL',
      vi: 'Tiếp cận người yêu thời trang nói tiếng Trung thông qua các chiến dịch KOL bắt trend và câu chuyện phong cách được kể một cách tinh tế'
    },
    industrySpaTitle: {
      en: 'Spa & Wellness',
      ja: 'スパ・ウェルネス',
      th: 'สปาและสุขภาพ',
      vi: 'Spa & Sức khỏe'
    },
    industrySpaDesc: {
      en: 'Drive appointments from Chinese-speaking guests through visual storytelling and reviews',
      ja: 'ビジュアルストーリーテリングとレビューで中華圏ゲストの予約を促進',
      th: 'กระตุ้นการจองจากแขกที่พูดภาษาจีนผ่านการเล่าเรื่องด้วยภาพและรีวิว',
      vi: 'Thu hút khách hàng nói tiếng Trung đến trải nghiệm dịch vụ của bạn bằng câu chuyện hình ảnh chân thật, cảm xúc và những đánh giá tích cực từ chính khách hàng'
    },
    serviceProcessTitle: {
      en: 'Service Process',
      ja: 'サービスプロセス',
      th: 'กระบวนการบริการ',
      vi: 'Quy trình dịch vụ'
    },
    serviceProcessSubtitle: {
      en: 'Our comprehensive approach to delivering successful social media marketing campaigns',
      ja: 'マーケティングを成功させるための包括的なアプローチ',
      th: 'แนวทางที่ครอบคลุมของเราในการส่งมอบแคมเปญการตลาดโซเชียลมีเดียที่ประสบความสำเร็จ',
      vi: "Chiến lược toàn diện giúp chúng tôi triển khai thành công các chiến dịch marketing trên mạng xã hội"
    },
    offlineChineseFriendlyTitle: {
      en: 'Offline Marketing – Chinese-Friendly<br />In-Store Experience Solutions',
      ja: 'オフラインマーケティング -<br />中華圏顧客向け顧客体験ソリューション',
      th: 'การตลาดออฟไลน์ -<br />โซลูชั่นประสบการณ์ในร้านที่เป็นมิตรกับลูกค้าจีน',
      vi: "Marketing ngoại tuyến –<br />Giải pháp trải nghiệm tại cửa hàng thân thiện với khách Trung Quốc"
    },
    onlineKolTitle: {
      en: 'Chinese Influencer (KOL/KOC) Marketing',
      ja: '中華圏インフルエンサー（KOL/KOC）マーケティング',
      th: 'การตลาดอินฟลูเอนเซอร์จีน (KOL/KOC)',
      vi: "Marketing Influencer Trung Quốc (KOL/KOC)"
    },
    // Offline Support content
    offlineDescription: {
      en: 'We design and produce Chinese-speaking in-store marketing materials, from standees to menus, and integrate them with Online-to-Offline promotions to boost satisfaction, sales, and customer loyalty.',
      ja: '中国語対応の店頭プロモーションツール（POPやメニューなど）をデザイン・制作し、オンラインとオフラインの施策を連動させ、顧客満足度・売上・ロイヤルティを向上させます。',
      th: 'เราออกแบบและผลิตสื่อการตลาดในร้านภาษาจีน ตั้งแต่แสตนดี้ไปจนถึงเมนู และผสานรวมกับโปรโมชั่นออนไลน์สู่ออฟไลน์เพื่อเพิ่มความพึงพอใจ ยอดขาย และความจงรักภักดีของลูกค้า',
      vi: "Chúng tôi thiết kế và sản xuất các ấn phẩm marketing tại cửa hàng bằng tiếng Trung, từ standee đến menu, đồng thời tích hợp với các chương trình Online-to-Offline nhằm nâng cao sự hài lòng, doanh số và lòng trung thành của khách hàng."
    },
    offlinePoint1: {
      en: 'Chinese language In-Store Material Design – Including standees, menus, flyers, price tags, and posters.',
      ja: '中国語店内販促物デザイン - スタンディ、メニュー、チラシ、価格タグ、ポスターを含む',
      th: 'การออกแบบวัสดุในร้านสำหรับลูกค้าที่พูดภาษาจีน - รวมถึงแสตนดี เมนู ใบปลิว ป้ายราคา และโปสเตอร์',
      vi: 'Thiết kế ấn phẩm tại cửa hàng bằng tiếng Trung – Bao gồm standee, menu, tờ rơi, bảng giá và poster.'
    },
    offlinePoint2: {
      en: 'Culturally Relevant Messaging – Language, visuals, and tone tailored for Chinese-speaking customers.',
      ja: '文化的背景に合わせたメッセージング - 中華圏顧客向けに言語、ビジュアル、トーンを最適化',
      th: 'การสื่อสารที่เกี่ยวข้องกับวัฒนธรรม - ภาษา ภาพ และน้ำเสียงที่ปรับให้เหมาะกับลูกค้าที่พูดภาษาจีน',
      vi: 'Thông điệp phù hợp văn hóa - Ngôn ngữ, hình ảnh và giọng điệu được điều chỉnh cho khách hàng nói tiếng Trung'
    },
    offlinePoint3: {
      en: 'Online-to-Offline Integration – QR codes and online links for promotions, loyalty programs, and booking systems.',
      ja: 'オンラインからオフラインへの統合 - プロモーション、ロイヤルティプログラム、予約システム用のQRコードやオンラインリンク',
      th: 'การบูรณาการออนไลน์สู่ออฟไลน์ - QR โค้ดและลิงก์ออนไลน์สำหรับโปรโมชัน โปรแกรมความภักดี และระบบการจอง',
      vi: 'Tích hợp Trực tuyến sang Ngoại tuyến - Mã QR và liên kết trực tuyến cho khuyến mãi, chương trình khách hàng thân thiết và hệ thống đặt chỗ'
    },
    offlinePoint4: {
      en: 'Sales & LTV Focus – Materials designed to encourage repeat visits and long-term loyalty.',
      ja: '売上とLTV（顧客生涯価値）へのフォーカス - リピート利用と長期的なロイヤルティを促進するよう設計された販促物',
      th: 'มุ่งเน้นยอดขายและ LTV - วัสดุที่ออกแบบมาเพื่อกระตุ้นการกลับมาซื้อซ้ำและความภักดีระยะยาว',
      vi: "Tập trung vào Doanh số & Giá trị trọn đời – Các ấn phẩm được thiết kế để khuyến khích khách quay lại và xây dựng lòng trung thành lâu dài."
    },
    // KOL/KOC content
    kolWhyYouNeed: {
      en: 'Why It Matters',
      ja: 'このサービスを選ぶ理由',
      th: 'ทำไมสิ่งนี้ถึงสำคัญ',
      vi: 'Tại sao điều này quan trọng'
    },
    kolIntro1: {
      en: 'When targeting Chinese-speaking customers, whether tourists or local residents, location matters.',
      ja: '観光客か在住者かを問わず、中華圏顧客を獲得するには、“現地に根ざした中華圏インフルエンサー”によるプロモーションが効果を最大化します。',
      th: 'เมื่อกำหนดเป้าหมายลูกค้าที่พูดภาษาจีน ไม่ว่าจะเป็นนักท่องเที่ยวหรือผู้อยู่อาศัยในท้องถิ่น สถานที่มีความสำคัญ',
      vi: "Khi tiếp cận khách hàng nói tiếng Trung, dù là khách du lịch hay cư dân địa phương, yếu tố địa điểm luôn quan trọng."
    },
    kolIntro2: {
      en: 'Influencers living in the target market can create content that is:',
      ja: 'ターゲット市場に住む中華圏KOL/KOCは、文化やトレンドに根ざした信頼性あるコンテンツを発信できます：',
      th: 'อินฟลูเอนเซอร์ที่อาศัยอยู่ในตลาดเป้าหมายสามารถสร้างเนื้อหาที่:',
      vi: "Những người có sức ảnh hưởng sống tại thị trường mục tiêu có thể tạo nội dung:"
    },
    kolPoint1: {
      en: 'Relevant to current local trends and local events',
      ja: 'ローカルトレンドやローカルイベントに関連したタイムリーな発信が可能',
      th: 'เกี่ยวข้องกับแนวโน้มและเหตุการณ์ท้องถิ่นปัจจุบัน',
      vi: 'Phù hợp với xu hướng và sự kiện địa phương hiện tại'
    },
    kolPoint2: {
      en: 'Easier for audiences to act on immediately (visit, book, shop)',
      ja: 'オーディエンスがすぐに行動しやすい（来店、予約、購入）',
      th: 'ง่ายต่อการให้ผู้ชมดำเนินการทันที (เยี่ยมชม จอง ซื้อ)',
      vi: "Thuận tiện để khách ra quyết định và hành động ngay (tham quan, đặt chỗ, mua sắm)"
    },
    kolPoint3: {
      en: 'Authentic because they experience the same environment as their followers will',
      ja: 'フォロワーと同じ環境を体験しているため信頼性が高い',
      th: 'น่าเชื่อถือเพราะพวกเขามีประสบการณ์ในสภาพแวดล้อมเดียวกันกับผู้ติดตาม',
      vi: 'Chân thực vì họ trải nghiệm cùng môi trường như người theo dõi của họ'
    },
    kolConclusion: {
      en: 'We specialize in working with Chinese KOLs (Key Opinion Leaders) and KOCs (Key Opinion Consumers) based in your market, giving your brand the power to influence purchase decisions in real time.',
      ja: '私たちはターゲット市場に根ざした中華圏KOL（キーオピニオンリーダー）とKOC（キーオピニオンコンシューマー）との協働を通して、ブランドに購買決定へ直結する影響力をもたらします。',
      th: 'เราเชี่ยวชาญในการทำงานกับ KOL (ผู้นำความคิดเห็นหลัก) และ KOC (ผู้บริโภคความคิดเห็นหลัก) ชาวจีนที่อยู่ในตลาดของคุณ ทำให้แบรนด์ของคุณมีพลังในการมีอิทธิพลต่อการตัดสินใจซื้อแบบเรียลไทม์',
      vi: "Với thế mạnh làm việc cùng các KOL và KOC Trung Quốc tại thị trường địa phương, chúng tôi giúp thương hiệu của bạn gia tăng ảnh hưởng và thúc đẩy quyết định mua sắm tức thì."
    },
    // What We Offer in KOL section
    kolWhatWeOffer: {
      en: 'What We Offer',
      ja: '私たちのソリューション',
      th: 'สิ่งที่เรานำเสนอ',
      vi: "Dịch vụ chúng tôi cung cấp"
    },
    kolOfferPoint1Title: {
      en: 'Local Influencer Matching',
      ja: 'ローカル中華圏インフルエンサーマッチング',
      th: 'การจับคู่อินฟลูเอนเซอร์ท้องถิ่น',
      vi: "Lựa chọn và kết hợp với KOL/KOC địa phương"
    },
    kolOfferPoint1Desc: {
      en: 'We identify the right KOL/KOC for your industry and target audience.',
      ja: '業界特性とターゲット顧客層に適したKOL/KOCを選定。',
      th: 'เราระบุ KOL/KOC ที่เหมาะสมสำหรับอุตสาหกรรมและกลุ่มเป้าหมายของคุณ',
      vi: 'Chúng tôi xác định KOL/KOC phù hợp cho ngành của bạn và đối tượng mục tiêu'
    },
    kolOfferPoint2Title: {
      en: 'Content Planning & Coordination',
      ja: 'コンテンツ企画・進行管理',
      th: 'การวางแผนและประสานงานเนื้อหา',
      vi: 'Lập kế hoạch & Phối hợp nội dung'
    },
    kolOfferPoint2Desc: {
      en: 'We align influencer posts with your brand message and goals.',
      ja: 'インフルエンサーの投稿をブランドメッセージや目標に整合。',
      th: 'เราจัดให้โพสต์ของอินฟลูเอนเซอร์สอดคล้องกับข้อความและเป้าหมายแบรนด์ของคุณ',
      vi: "Chúng tôi điều chỉnh bài đăng của KOL/KOC phù hợp với thông điệp và mục tiêu thương hiệu của bạn"
    },
    kolOfferPoint3Title: {
      en: 'Multi-Platform Coverage',
      ja: 'マルチプラットフォームカバレッジ',
      th: 'การครอบคลุมหลายแพลตฟอร์ม',
      vi: "Phủ sóng đa nền tảng"
    },
    kolOfferPoint3Desc: {
      en: 'Including RedBook(Xiaohongshu), Douyin, WeChat, Weibo, and more.',
      ja: 'RedBook(Xiaohongshu)、Douyin、WeChat、Weiboなどを含むソリューションを提供。',
      th: 'รวมถึง RedBook(Xiaohongshu), Douyin, WeChat, Weibo และอื่นๆ',
      vi: 'Bao gồm RedBook(Xiaohongshu), Douyin, WeChat, Weibo và nhiều hơn nữa'
    },
    kolOfferPoint4Title: {
      en: 'Authentic Storytelling',
      ja: '心を動かすストーリーテリング',
      th: 'การเล่าเรื่องที่แท้จริง',
      vi: "Nội dung truyền tải chân thực"
    },
    kolOfferPoint4Desc: {
      en: 'Influencers experience your products or services and share genuine feedback.',
      ja: 'インフルエンサーが製品・サービスを体験し、信頼できるフィードバックを提供。',
      th: 'อินฟลูเอนเซอร์ทดลองผลิตภัณฑ์หรือบริการของคุณและแบ่งปันความคิดเห็นที่แท้จริง',
      vi: "KOL/KOC trải nghiệm sản phẩm hoặc dịch vụ của bạn và chia sẻ phản hồi chân thực"
    },
    kolOfferPoint5Title: {
      en: 'Performance Tracking',
      ja: 'パフォーマンス追跡',
      th: 'การติดตามประสิทธิภาพ',
      vi: 'Theo dõi hiệu suất'
    },
    kolOfferPoint5Desc: {
      en: 'We monitor reach, engagement, and conversions from influencer content.',
      ja: 'インフルエンサーコンテンツからのリーチ、エンゲージメント、コンバージョンを測定。',
      th: 'เราติดตามการเข้าถึง การมีส่วนร่วม และการแปลงจากเนื้อหาอินฟลูเอนเซอร์',
      vi: "Chúng tôi giám sát hiệu quả nội dung của KOL/KOC qua các chỉ số: lượt tiếp cận, tương tác và chuyển đổi."
    },
    // Service Process
    serviceProcess: {
      en: 'Service Process',
      ja: 'サービスプロセス',
      th: 'ขั้นตอนการบริการ',
      vi: 'Quy trình dịch vụ'
    },
    serviceProcessDesc: {
      en: 'Our comprehensive approach to delivering successful social media marketing campaigns',
      ja: '成功に導くSNSマーケティングキャンペーンのための包括的アプローチ。',
      th: 'แนวทางที่ครอบคลุมของเราในการส่งมอบแคมเปญการตลาดโซเชียลมีเดียที่ประสบความสำเร็จ',
      vi: 'Phương pháp toàn diện của chúng tôi để thực hiện các chiến dịch tiếp thị mạng xã hội thành công'
    },
    processStep1Title: {
      en: 'Initial Consultation & Strategy Setup',
      ja: '初期コンサルティングとAI・データ戦略設計',
      th: 'การปรึกษาเบื้องต้นและการตั้งค่ากลยุทธ์',
      vi: 'Tư vấn ban đầu & Thiết lập chiến lược'
    },
    processStep1Desc: {
      en: 'We define your target audience, brand objectives, and platform mix, then build a content strategy tailored to the Chinese-speaking market.',
      ja: 'ターゲットオーディエンス、検索行動（AI Search / GEO）、ブランド目標、SNS導線、O2O動線を定義し、テクノロジーを活用した統合型マーケティング戦略を構築。',
      th: 'เรากำหนดกลุ่มเป้าหมาย วัตถุประสงค์แบรนด์ และการผสมผสานแพลตฟอร์ม จากนั้นสร้างกลยุทธ์เนื้อหาที่ปรับให้เหมาะกับตลาดที่พูดภาษาจีน',
      vi: 'Chúng tôi xác định đối tượng mục tiêu, mục tiêu thương hiệu và kết hợp nền tảng, sau đó xây dựng chiến lược nội dung được tùy chỉnh cho thị trường nói tiếng Trung'
    },
    processStep2Title: {
      en: 'Account Setup or Optimization',
      ja: 'アカウント設定と最適化',
      th: 'การตั้งค่าหรือเพิ่มประสิทธิภาพบัญชี',
      vi: 'Thiết lập hoặc Tối ưu hóa tài khoản'
    },
    processStep2Desc: {
      en: 'We create new accounts or audit existing ones on RedBook(Xiaohongshu), WeChat, Weibo, and Douyin, ensuring profiles are brand-aligned, optimized, and keyword-rich.',
      ja: '各SNSプラットフォームでアカウントを新規構築し、AI検索で見つかりやすい構造に最適化。ブランドに即したキーワード強化やタグ設計も実施。',
      th: 'เราสร้างบัญชีใหม่หรือตรวจสอบบัญชีที่มีอยู่บน RedBook(Xiaohongshu), WeChat, Weibo และ Douyin โดยตรวจสอบให้แน่ใจว่าโปรไฟล์สอดคล้องกับแบรนด์ ถูกปรับให้เหมาะสม และอุดมไปด้วยคำหลัก',
      vi: 'Chúng tôi tạo tài khoản mới hoặc kiểm tra các tài khoản hiện có trên RedBook(Xiaohongshu), WeChat, Weibo và Douyin, đảm bảo hồ sơ phù hợp với thương hiệu, được tối ưu hóa và giàu từ khóa'
    },
    processStep3Title: {
      en: 'Monthly Content Planning',
      ja: '月次コンテンツ計画（O2O）',
      th: 'การวางแผนเนื้อหารายเดือน',
      vi: 'Lập kế hoạch nội dung hàng tháng'
    },
    processStep3Desc: {
      en: 'We prepare a rolling monthly content calendar with localized copywriting, professional visuals, and seasonal/Online-to-Offline tie-ins to drive both online engagement and in-store activity.',
      ja: 'ローカライズされたコピー、ビジュアル、シーズナル企画、GEO検索対策、O2O導線を盛り込んだ月次コンテンツカレンダーを作成し、店舗送客やEC導線を強化。',
      th: 'เราจัดเตรียมปฏิทินเนื้อหารายเดือนแบบต่อเนื่องที่มีการเขียนข้อความที่เหมาะกับท้องถิ่น ภาพมืออาชีพ และการเชื่อมโยงตามฤดูกาล/ออนไลน์สู่ออฟไลน์เพื่อกระตุ้นทั้งการมีส่วนร่วมออนไลน์และกิจกรรมในร้าน',
      vi: 'Chúng tôi chuẩn bị lịch nội dung hàng tháng liên tục với nội dung được bản địa hóa, hình ảnh chuyên nghiệp và kết nối theo mùa/Trực tuyến sang Ngoại tuyến để thúc đẩy cả tương tác trực tuyến và hoạt động tại cửa hàng'
    },
    processStep4Title: {
      en: 'Content Production & Publishing',
      ja: 'コンテンツ制作とマルチチャネル配信',
      th: 'การผลิตและเผยแพร่เนื้อหา',
      vi: 'Sản xuất & Xuất bản nội dung'
    },
    processStep4Desc: {
      en: 'We create high-quality content (images, videos, copy) and publish consistently on each platform, adjusting formats to match platform algorithms and trends.',
      ja: '画像・動画・コピーを高品質で制作し、AI最適化フォーマットで各プラットフォームへ継続的に配信。アルゴリズムやトレンド、検索行動データに合わせて随時チューニング。',
      th: 'เราผลิตเนื้อหาคุณภาพสูง (รูปภาพ วิดีโอ ข้อความ) และเผยแพร่อย่างสม่ำเสมอบนแต่ละแพลตฟอร์ม โดยปรับรูปแบบให้เหมาะกับอัลกอริทึมและแนวโน้มของแพลตฟอร์ม',
      vi: 'Chúng tôi tạo nội dung chất lượng cao (hình ảnh, video, văn bản) và xuất bản nhất quán trên mỗi nền tảng, điều chỉnh định dạng phù hợp với thuật toán và xu hướng của nền tảng'
    },
    processStep5Title: {
      en: 'Engagement & Community Management',
      ja: 'エンゲージメントとコミュニティ管理',
      th: 'การมีส่วนร่วมและการจัดการชุมชน',
      vi: 'Tương tác & Quản lý cộng đồng'
    },
    processStep5Desc: {
      en: 'We monitor comments and messages, respond in a timely and culturally appropriate manner, and actively engage with followers to foster trust and loyalty.',
      ja: 'コメントやメッセージを適切にモニタリングし、文化的ニュアンスを踏まえた返信でファンとの積極的な交流を促進。継続的な信頼とロイヤルティ形成を支援。',
      th: 'เราติดตามความคิดเห็นและข้อความ ตอบกลับอย่างทันเวลาและเหมาะสมทางวัฒนธรรม และมีส่วนร่วมกับผู้ติดตามอย่างกระตือรือร้นเพื่อสร้างความไว้วางใจและความภักดี',
      vi: 'Chúng tôi giám sát bình luận và tin nhắn, phản hồi kịp thời và phù hợp về văn hóa, tích cực tương tác với người theo dõi để xây dựng niềm tin và lòng trung thành'
    },
    processStep6Title: {
      en: 'Online-to-Offline Integration',
      ja: 'オンラインからオフライン・ECへの統合導線設計',
      th: 'การบูรณาการออนไลน์สู่ออฟไลน์',
      vi: 'Tích hợp Trực tuyến sang Ngoại tuyến'
    },
    processStep6Desc: {
      en: 'We connect online activity to offline sales through in-store promotions, QR code campaigns, seasonal events, and tourism-related offers.',
      ja: 'QRコード施策、来店意欲喚起コンテンツ、キャンペーン、O2O動線、ECリンクを組み合わせ、SNSで得た関心を店舗来訪やEC購買へ転換。オフラインの成果をオンラインへ循環させる設計も実施。',
      th: 'เราเชื่อมต่อกิจกรรมออนไลน์กับยอดขายออฟไลน์ผ่านโปรโมชันในร้าน แคมเปญ QR โค้ด กิจกรรมตามฤดูกาล และข้อเสนอที่เกี่ยวข้องกับการท่องเที่ยว',
      vi: "Chúng tôi kết nối hoạt động trực tuyến với doanh số ngoại tuyến thông qua các chương trình khuyến mãi tại cửa hàng, chiến dịch QR code, sự kiện theo mùa và ưu đãi dành cho khách du lịch"
    },
    processStep7Title: {
      en: 'Performance Review & Optimization',
      ja: 'パフォーマンスレビューとデータドリブン最適化',
      th: 'การตรวจสอบและเพิ่มประสิทธิภาพ',
      vi: 'Đánh giá hiệu suất & Tối ưu hóa'
    },
    processStep7Desc: {
      en: 'We provide monthly reports tracking reach, engagement, follower growth, and offline impact, and refine strategies for continuous improvement.',
      ja: '月次レポートでリーチ、エンゲージメント、来店・購買データ、フォロワー推移などを可視化し、AI分析とKPI管理ダッシュボードを活用して継続的な成果向上のために戦略を改善。',
      th: 'เราจัดทำรายงานประจำเดือนติดตามการเข้าถึง การมีส่วนร่วม การเติบโตของผู้ติดตาม และผลกระทบออฟไลน์ และปรับปรุงกลยุทธ์เพื่อการปรับปรุงอย่างต่อเนื่อง',
      vi: 'Chúng tôi cung cấp báo cáo hàng tháng theo dõi phạm vi tiếp cận, tương tác, tăng trưởng người theo dõi và tác động ngoại tuyến, và tinh chỉnh chiến lược để cải tiến liên tục'
    },
    // Buttons
    viewCaseStudies: {
      en: 'View Case Studies',
      ja: 'ケーススタディを見る',
      th: 'ดูกรณีศึกษา',
      vi: "Xem các ví dụ điển hình"
    },
    hideCaseStudies: {
      en: 'Hide Case Studies',
      ja: 'ケーススタディを非表示',
      th: 'ซ่อนกรณีศึกษา',
      vi: "Ẩn các ví dụ điển hình"
    },
    startYourCampaign: {
      en: 'Start Your Campaign',
      ja: 'キャンペーンを開始',
      th: '',
      vi: 'Triển khai chiến dịch của bạn'
    }
  },
  // Search Ads & Media Outreach page
  searchAds: {
    heroTitle: {
      en: 'Chinese Search Engine (Baidu) Ads & Media Outreach – Be Visible, Accessible, and Relevant in China',
      ja: '中国検索エンジン(Baidu)広告・PR支援 ― 中華圏市場で発見され、つながり、信頼されるために',
      th: 'โฆษณาเครื่องมือค้นหาจีน (Baidu) และการเข้าถึงสื่อ - ให้ปรากฏ เข้าถึงได้ และมีความเกี่ยวข้องในประเทศจีน',
      vi: 'Quảng cáo Công cụ Tìm kiếm Trung Quốc (Baidu) & Tiếp cận Truyền thông - Hiển thị, Dễ tiếp cận và Phù hợp tại Trung Quốc'
    },
    heroDescription: {
      en: 'We help you reach high-intent Chinese customers through Baidu ads and Chinese-hosted landing pages — ensuring your brand is accessible, fast, and persuasive for users in China.',
      ja: 'Baidu広告と中国国内サーバーでのランディングページ運用により、購買意欲の高い中国顧客にリーチ。アクセス性・表示スピード・説得力を兼ね備えたブランドを実現します。',
      th: 'เราช่วยให้คุณเข้าถึงลูกค้าจีนที่มีความตั้งใจสูงผ่านโฆษณา Baidu และหน้าแลนดิ้งที่โฮสต์ในจีน - ทำให้แบรนด์ของคุณเข้าถึงได้ รวดเร็ว และน่าเชื่อถือสำหรับผู้ใช้ในประเทศจีน',
      vi: 'Chúng tôi giúp bạn tiếp cận khách hàng Trung Quốc có ý định cao thông qua quảng cáo Baidu và trang đích được lưu trữ tại Trung Quốc - đảm bảo thương hiệu của bạn dễ tiếp cận, nhanh chóng và thuyết phục với người dùng tại Trung Quốc.'
    },
    whyThisMatters: {
      en: 'Why It Matters',
      ja: 'なぜ重要なのか',
      th: 'ทำไมสิ่งนี้ถึงสำคัญ',
      vi: 'Tại sao điều này quan trọng'
    },
    whyThisMattersPoint1: {
      en: 'Baidu is China\'s largest search engine, with over 650 million monthly active users. It is where Chinese-speaking customers search for brands, compare options, and make purchase decisions.',
      ja: 'Baiduは月間アクティブユーザー数6億5000万人以上を誇る、中国最大の検索エンジンです。中華圏顧客がブランドを検索し、比較検討し、購買を決定する主要なツールとなっています。',
      th: 'Baidu เป็นเครื่องมือค้นหาที่ใหญ่ที่สุดของจีน มีผู้ใช้งานต่อเดือนกว่า 650 ล้านคน เป็นที่ที่ลูกค้าที่พูดภาษาจีนค้นหาแบรนด์ เปรียบเทียบตัวเลือก และตัดสินใจซื้อ',
      vi: 'Baidu là công cụ tìm kiếm lớn nhất của Trung Quốc, với hơn 650 triệu người dùng hoạt động hàng tháng. Đây là nơi khách hàng nói tiếng Trung tìm kiếm thương hiệu, so sánh lựa chọn và đưa ra quyết định mua hàng.'
    },
    whyThisMattersPoint2: {
      en: 'Many non-Chinese websites are slow to load or completely inaccessible through Chinese search engines due to hosting restrictions and internet policies. Even if you have a professional corporate website, your potential Chinese customers might not be able to access it, losing you thousands in potential revenue.',
      ja: '中国のインターネット政策の影響で、多くの海外ウェブサイトは中国国内からのアクセスが極めて遅い、もしくは不可能です。そのため、せっかくの企業サイトがあっても中国の潜在顧客に届かず、大きな収益機会を失う可能性があります。',
      th: 'เว็บไซต์ที่ไม่ใช่ของจีนหลายแห่งโหลดช้าหรือไม่สามารถเข้าถึงได้เลยผ่านเครื่องมือค้นหาของจีน เนื่องจากข้อจำกัดด้านโฮสติ้งและนโยบายอินเทอร์เน็ต แม้คุณจะมีเว็บไซต์องค์กรที่เป็นมืออาชีพ ลูกค้าชาวจีนที่มีศักยภาพของคุณอาจไม่สามารถเข้าถึงได้ ทำให้คุณสูญเสียรายได้ที่เป็นไปได้หลายพันดอลลาร์',
      vi: 'Nhiều trang web không phải của Trung Quốc tải chậm hoặc hoàn toàn không thể truy cập thông qua công cụ tìm kiếm Trung Quốc do hạn chế lưu trữ và chính sách internet. Ngay cả khi bạn có trang web doanh nghiệp chuyên nghiệp, khách hàng Trung Quốc tiềm năng có thể không thể truy cập, khiến bạn mất hàng nghìn doanh thu tiềm năng.'
    },
    whyThisMattersPoint3: {
      en: 'Our solution: We combine Baidu advertising with fast-loading, Chinese-hosted landing pages. This ensures that every click from your ad leads to a seamless, accessible user experience — boosting both engagement and conversions.',
      ja: '私たちは、Baidu広告と高速読み込みの中国ホスティング型ランディングページを組み合わせ、広告からのクリックをシームレスで確実にアクセスできる体験へと変換し、エンゲージメントとコンバージョンを強化します。',
      th: 'โซลูชั่นของเรา: เรารวมการโฆษณา Baidu กับหน้าแลนดิ้งที่โหลดเร็วและโฮสต์ในจีน สิ่งนี้ทำให้มั่นใจได้ว่าทุกคลิกจากโฆษณาของคุณนำไปสู่ประสบการณ์ผู้ใช้ที่ราบรื่นและเข้าถึงได้ - เพิ่มทั้งการมีส่วนร่วมและการแปลง',
      vi: "Giải pháp: Chúng tôi tích hợp quảng cáo Baidu cùng trang đích lưu trữ trong nước với tốc độ tải nhanh, giúp mỗi cú nhấp chuột từ quảng cáo mang lại trải nghiệm mượt mà, dễ tiếp cận và tối ưu hóa tương tác cũng như chuyển đổi."
    },
    didYouKnow: {
      en: 'Did You Know?',
      ja: 'ご存知でしたか？',
      th: 'คุณรู้หรือไม่?',
      vi: 'Bạn có biết?'
    },
    didYouKnowContent: {
      en: 'Over 70% of foreign websites are inaccessible or extremely slow for users in China. Without a Chinese-language landing page, your marketing reach can drop dramatically — even if your website works perfectly outside of China.',
      ja: '外国のウェブサイトの70％以上が中国のユーザーにとってアクセスできないか、非常に遅いです。中国語のランディングページがなければ、中国以外で完璧に機能するウェブサイトでも、マーケティングリーチが劇的に低下する可能性があります。',
      th: 'เว็บไซต์ต่างประเทศกว่า 70% ไม่สามารถเข้าถึงได้หรือช้ามากสำหรับผู้ใช้ในจีน หากไม่มีหน้าแลนดิ้งภาษาจีน การเข้าถึงทางการตลาดของคุณอาจลดลงอย่างมาก - แม้ว่าเว็บไซต์ของคุณจะทำงานได้อย่างสมบูรณ์นอกประเทศจีน',
      vi: "Hơn 70% trang web nước ngoài không thể truy cập được hoặc cực kỳ chậm đối với người dùng tại Trung Quốc. Không có trang đích tiếng Trung, phạm vi marketing của bạn có thể giảm đáng kể - ngay cả khi trang web của bạn hoạt động hoàn hảo bên ngoài Trung Quốc."
    },
    whatWeOffer: {
      en: 'What We Offer',
      ja: '私たちが提供するもの',
      th: 'สิ่งที่เรานำเสนอ',
      vi: "Dịch vụ chúng tôi cung cấp"
    },
    offerPoint1Title: {
      en: 'Official Baidu Ads Account Setup',
      ja: '公式Baidu広告アカウント開設',
      th: 'การตั้งค่าบัญชีโฆษณา Baidu อย่างเป็นทางการ',
      vi: "Thiết lập tài khoản quảng cáo Baidu chính thức"
    },
    offerPoint1Desc: {
      en: 'Launch without needing a China-based business entity.',
      ja: '中国での法人設立なしでスタートできます。',
      th: 'เปิดตัวได้โดยไม่ต้องมีนิติบุคคลในจีน',
      vi: 'Khởi chạy mà không cần pháp nhân doanh nghiệp tại Trung Quốc.'
    },
    offerPoint2Title: {
      en: 'China-Accessible Landing Page Design',
      ja: '中国アクセス可能なランディングページデザイン',
      th: 'การออกแบบหน้าแลนดิ้งที่เข้าถึงได้ในจีน',
      vi: "Thiết kế trang đích có thể truy cập tại Trung Quốc"
    },
    offerPoint2Desc: {
      en: 'PC and mobile pages hosted in China for fast access.',
      ja: 'PC・モバイル双方に対応した、中国国内ホスティングの高速アクセスページ',
      th: 'หน้าเว็บสำหรับ PC และมือถือที่โฮสต์ในจีนเพื่อการเข้าถึงที่รวดเร็ว',
      vi: 'Trang PC và di động được lưu trữ tại Trung Quốc để truy cập nhanh.'
    },
    offerPoint3Title: {
      en: 'Search Strategy Development',
      ja: '検索戦略策定と実行',
      th: 'การพัฒนากลยุทธ์การค้นหา',
      vi: "Phát triển chiến lược tìm kiếm"
    },
    offerPoint3Desc: {
      en: 'Targeted keywords based on market segmentation, consumer behavior, and competitor research.',
      ja: '市場セグメンテーション、消費者行動、競合調査に基づいたターゲットキーワード選定',
      th: 'คีย์เวิร์ดเป้าหมายที่อิงจากการแบ่งส่วนตลาด พฤติกรรมผู้บริโภค และการวิจัยคู่แข่ง',
      vi: 'Từ khóa mục tiêu dựa trên phân khúc thị trường, hành vi người tiêu dùng và nghiên cứu đối thủ.'
    },
    offerPoint4Title: {
      en: 'Continuous Optimization',
      ja: '継続的な改善と最適化',
      th: 'การปรับปรุงอย่างต่อเนื่อง',
      vi: "Tối ưu hóa liên tục"
    },
    offerPoint4Desc: {
      en: 'Managed by Baidu SEM specialists for the best ROI.',
      ja: 'Baidu SEMのスペシャリストが運用を担当し、最適なROIを実現',
      th: 'จัดการโดยผู้เชี่ยวชาญ Baidu SEM เพื่อ ROI ที่ดีที่สุด',
      vi: "Được quản lý bởi đội ngũ chuyên gia Baidu SEM, đảm bảo hiệu quả đầu tư tối ưu."
    },
    offerPoint5Title: {
      en: 'Performance Insights',
      ja: 'パフォーマンスインサイト',
      th: 'ข้อมูลเชิงลึกด้านประสิทธิภาพ',
      vi: "Phân tích hiệu suất"
    },
    offerPoint5Desc: {
      en: 'Monthly reports with actionable recommendations.',
      ja: '実行可能な改善提案を含む月次レポート',
      th: 'รายงานรายเดือนพร้อมคำแนะนำที่นำไปปฏิบัติได้',
      vi: 'Báo cáo hàng tháng với các khuyến nghị có thể thực hiện.'
    },
    searchEngineMarketing: {
      en: 'Chinese Search Engine (Baidu) Marketing',
      ja: '検索エンジンマーケティング',
      th: 'การตลาดเครื่องมือค้นหา',
      vi: 'Marketing trên công cụ tìm kiếm (Baidu)'
    },
    baiduAds: {
      en: 'Baidu Ads',
      ja: 'Baidu広告',
      th: 'โฆษณา Baidu',
      vi: 'Quảng cáo Baidu'
    },
    baiduDesc: {
      en: 'China\'s largest search engine with over 70% market share',
      ja: '市場シェア70％超を誇る、中国最大の検索エンジン',
      th: 'เครื่องมือค้นหาที่ใหญ่ที่สุดของจีนมีส่วนแบ่งตลาดกว่า 70%',
      vi: 'Công cụ tìm kiếm lớn nhất Trung Quốc với hơn 70% thị phần'
    },
    sogouAds: {
      en: 'Sogou Ads',
      ja: 'Sogou広告',
      th: 'โฆษณา Sogou',
      vi: 'Quảng cáo Sogou'
    },
    sogouDesc: {
      en: 'The second-largest search engine integrated with WeChat',
      ja: 'WeChatと統合された第2位の検索エンジン',
      th: 'เครื่องมือค้นหาใหญ่เป็นอันดับสองที่รวมกับ WeChat',
      vi: 'Công cụ tìm kiếm lớn thứ hai tích hợp với WeChat'
    },
    shenmaAds: {
      en: 'Shenma Ads',
      ja: 'Shenma広告',
      th: 'โฆษณา Shenma',
      vi: 'Quảng cáo Shenma'
    },
    shenmaDesc: {
      en: 'Mobile-first search engine by Alibaba',
      ja: 'アリババのモバイルファースト検索エンジン',
      th: 'เครื่องมือค้นหาบนมือถือโดย Alibaba',
      vi: 'Công cụ tìm kiếm di động của Alibaba'
    },
    searchServices: {
      en: 'Search Services',
      ja: '検索サービス',
      th: 'บริการค้นหา',
      vi: 'Dịch vụ tìm kiếm'
    },
    keywordResearch: {
      en: 'Keyword Research',
      ja: 'キーワードリサーチ',
      th: 'การวิจัยคำหลัก',
      vi: 'Nghiên cứu từ khóa'
    },
    adCopywriting: {
      en: 'Ad Copywriting',
      ja: '広告コピーライティング',
      th: 'การเขียนโฆษณา',
      vi: 'Viết quảng cáo'
    },
    campaignOptimization: {
      en: 'Campaign Optimization',
      ja: 'キャンペーン最適化',
      th: 'การเพิ่มประสิทธิภาพแคมเปญ',
      vi: 'Tối ưu hóa chiến dịch'
    },
    performanceTracking: {
      en: 'Performance Tracking',
      ja: 'パフォーマンス追跡',
      th: 'การติดตามประสิทธิภาพ',
      vi: 'Theo dõi hiệu suất'
    },
    mediaOutreach: {
      en: 'Media Outreach',
      ja: 'メディアPR',
      th: 'การเข้าถึงสื่อ',
      vi: 'Tiếp cận truyền thông'
    },
    newsPortals: {
      en: 'Chinese News Portals',
      ja: '中国ニュースポータル',
      th: 'เว็บไซต์ข่าวจีน',
      vi: 'Cổng tin tức Trung Quốc'
    },
    pressReleases: {
      en: 'Press Releases',
      ja: 'プレスリリース',
      th: 'ข่าวประชาสัมพันธ์',
      vi: 'Thông cáo báo chí'
    },
    mediaPartners: {
      en: 'Media Partners',
      ja: 'メディアパートナー',
      th: 'พันธมิตรสื่อ',
      vi: 'Đối tác truyền thông'
    },
    xinhua: {
      en: 'Xinhua News',
      ja: '新華社通信',
      th: 'ข่าวซินหัว',
      vi: 'Tin tức Tân Hoa Xã'
    },
    peoplesDaily: {
      en: 'People\'s Daily',
      ja: '人民日報',
      th: 'หนังสือพิมพ์เรินหมินรื่อเป่า',
      vi: 'Nhân Dân Nhật Báo'
    },
    sinaNews: {
      en: 'Sina News',
      ja: 'Sina新聞',
      th: 'ข่าว Sina',
      vi: 'Tin tức Sina'
    },
    sohuNews: {
      en: 'Sohu News',
      ja: 'Sohu新聞',
      th: 'ข่าว Sohu',
      vi: 'Tin tức Sohu'
    },
    mediaServices: {
      en: 'Media Services',
      ja: 'メディアサービス',
      th: 'บริการสื่อ',
      vi: 'Dịch vụ truyền thông'
    },
    contentCreationMedia: {
      en: 'Content Creation',
      ja: 'コンテンツ作成',
      th: 'การสร้างเนื้อหา',
      vi: 'Tạo nội dung'
    },
    mediaDistribution: {
      en: 'Media Distribution',
      ja: 'メディア配信',
      th: 'การเผยแพร่สื่อ',
      vi: 'Phân phối truyền thông'
    },
    brandStorytelling: {
      en: 'Brand Storytelling',
      ja: 'ブランドストーリーテリング',
      th: 'การเล่าเรื่องแบรนด์',
      vi: 'Kể chuyện thương hiệu'
    },
    crisisManagement: {
      en: 'Crisis Management',
      ja: '危機管理',
      th: 'การจัดการวิกฤต',
      vi: 'Quản lý khủng hoảng'
    },
    whyChooseOurServices: {
      en: 'Why Choose Our Services?',
      ja: '私たちが選ばれる理由',
      th: 'ทำไมต้องเลือกบริการของเรา?',
      vi: 'Tại sao chọn dịch vụ của chúng tôi?'
    },
    localExpertise: {
      en: 'Local Expertise',
      ja: '現地に根ざした知見',
      th: 'ความเชี่ยวชาญในท้องถิ่น',
      vi: "Am hiểu địa phương"
    },
    localExpertiseDesc: {
      en: 'Deep understanding of Chinese search behavior and media landscape',
      ja: '中国の検索行動とメディア環境を深く理解',
      th: 'ความเข้าใจอย่างลึกซึ้งเกี่ยวกับพฤติกรรมการค้นหาและภูมิทัศน์สื่อของจีน',
      vi: "Hiểu biết sâu sắc về hành vi tìm kiếm của người dùng Trung Quốc và bức tranh truyền thông"
    },
    provenResults: {
      en: 'Proven Results',
      ja: '確かな成果',
      th: 'ผลลัพธ์ที่พิสูจน์แล้ว',
      vi: "Hiệu quả được khẳng định"
    },
    provenResultsDesc: {
      en: 'Track record of successful campaigns across industries',
      ja: '幅広い業界での成功キャンペーン実績',
      th: 'ประวัติความสำเร็จของแคมเปญในหลากหลายอุตสาหกรรม',
      vi: "Kinh nghiệm thực hiện các chiến dịch thành công đa lĩnh vực"
    },
    integratedApproach: {
      en: 'Integrated Approach',
      ja: '統合的アプローチ',
      th: 'แนวทางแบบบูรณาการ',
      vi: 'Phương pháp tích hợp'
    },
    integratedApproachDesc: {
      en: 'Seamless coordination between search and media strategies',
      ja: '検索とメディア戦略のシームレスな連携',
      th: 'การประสานงานระหว่างกลยุทธ์การค้นหาและสื่ออย่างไร้รอยต่อ',
      vi: 'Phối hợp liền mạch giữa chiến lược tìm kiếm và truyền thông'
    },
    transparentReporting: {
      en: 'Transparent Reporting',
      ja: '明確なレポート',
      th: 'การรายงานที่โปร่งใส',
      vi: 'Báo cáo minh bạch'
    },
    transparentReportingDesc: {
      en: 'Regular updates and detailed performance metrics',
      ja: '定期更新と詳細なパフォーマンス分析',
      th: 'การอัพเดทเป็นประจำและตัวชี้วัดประสิทธิภาพโดยละเอียด',
      vi: 'Cập nhật thường xuyên và chỉ số hiệu suất chi tiết'
    },
    // Additional translations for the component
    searchEngineMarketingDesc: {
      en: 'Dominate Chinese search results with strategic advertising, optimized landing pages, and comprehensive SEO',
      ja: '戦略的広告と最適化ランディングページ、包括的SEOにより、中国の検索結果で優位性を確立',
      th: 'ครองผลการค้นหาของจีนด้วยการโฆษณาเชิงกลยุทธ์ หน้าแรกที่เพิ่มประสิทธิภาพ และ SEO ที่ครอบคลุม',
      vi: "Dẫn đầu kết quả tìm kiếm tại Trung Quốc với quảng cáo chiến lược, trang đích tối ưu và SEO toàn diện"
    },
    searchEngineAdvertising: {
      en: 'Search Engine Advertising',
      ja: '検索エンジン広告',
      th: 'การโฆษณาบนเครื่องมือค้นหา',
      vi: "Quảng cáo trên công cụ tìm kiếm"
    },
    searchEngineAdvertisingDesc: {
      en: 'Targeted advertising campaigns on Baidu, China’s major search platform, to capture high-intent users.',
      ja: 'Baidu、Sogouをはじめとする主要中国検索プラットフォームでのターゲティング広告で、高意欲ユーザーを確実に獲得',
      th: 'แคมเปญโฆษณาแบบกำหนดเป้าหมายบน Baidu แพลตฟอร์มค้นหาหลักของจีน เพื่อเข้าถึงผู้ใช้ที่มีความตั้งใจสูง',
      vi: 'Chiến dịch quảng cáo có mục tiêu trên Baidu và các nền tảng tìm kiếm lớn của Trung Quốc để thu hút người dùng có ý định cao.'
    },
    landingPageCreation: {
      en: 'Landing Page Creation',
      ja: 'ランディングページ作成',
      th: 'การสร้างหน้าแรก',
      vi: 'Tạo trang đích'
    },
    landingPageCreationDesc: {
      en: 'High-converting Chinese-optimized landing pages designed for cultural preferences and mobile-first user experience.',
      ja: '文化的嗜好とモバイルファーストのユーザー体験に最適化した、高コンバージョンの中華圏市場向けランディングページ',
      th: 'หน้าแรกที่เพิ่มประสิทธิภาพสำหรับจีนที่ออกแบบตามความชอบทางวัฒนธรรมและประสบการณ์ผู้ใช้บนมือถือ',
      vi: 'Trang đích tối ưu cho Trung Quốc có tỷ lệ chuyển đổi cao, được thiết kế theo sở thích văn hóa và trải nghiệm người dùng di động'
    },
    seoOptimization: {
      en: 'SEO, Search Result Optimization',
      ja: 'SEO、検索結果最適化',
      th: 'SEO, การเพิ่มประสิทธิภาพผลการค้นหา',
      vi: 'SEO, Tối ưu kết quả tìm kiếm'
    },
    seoOptimizationDesc: {
      en: 'Comprehensive SEO strategies tailored for Chinese search engines to improve organic visibility and drive qualified traffic.',
      ja: '中国の検索エンジンに特化した包括SEO戦略でオーガニックの可視性を向上させ、質の高いトラフィックを獲得',
      th: 'กลยุทธ์ SEO ที่ครอบคลุมที่ปรับให้เหมาะกับเครื่องมือค้นหาของจีนเพื่อเพิ่มการมองเห็นแบบออร์แกนิคและดึงดูดการเข้าชมที่มีคุณภาพ',
      vi: 'Chiến lược SEO toàn diện được tùy chỉnh cho các công cụ tìm kiếm Trung Quốc để cải thiện khả năng hiển thị tự nhiên và thu hút lưu lượng chất lượng'
    },
    prMediaOutreach: {
      en: 'Media Outreach, PR',
      ja: 'メディアアウトリーチ、PR',
      th: 'การเข้าถึงสื่อ, ประชาสัมพันธ์',
      vi: 'Tiếp cận truyền thông, PR'
    },
    prMediaOutreachDesc: {
      en: 'Strategic media placement and public relations campaigns to amplify your brand presence across Chinese media landscapes and establish market authority.',
      ja: '中国メディア全体でブランドの存在感を高め、市場での信頼と権威を確立する戦略的メディア展開とPRキャンペーン',
      th: 'การวางสื่อเชิงกลยุทธ์และแคมเปญประชาสัมพันธ์เพื่อขยายการมีอยู่ของแบรนด์ทั่วภูมิทัศน์สื่อของจีนและสร้างอำนาจตลาด',
      vi: "Triển khai chiến lược truyền thông và PR để gia tăng độ phủ thương hiệu trên toàn bộ hệ sinh thái truyền thông Trung Quốc, đồng thời xây dựng uy tín thị trường"
    },
    serviceProcess: {
      en: 'Service Process (For Baidu Ads)',
      ja: 'サービスプロセス（Baidu広告向け）',
      th: 'ขั้นตอนการบริการ (สำหรับโฆษณา Baidu)',
      vi: 'Quy trình dịch vụ (Cho quảng cáo Baidu)'
    },
    viewCaseStudies: {
      en: 'View Case Studies',
      ja: 'ケーススタディを見る',
      th: 'ดูกรณีศึกษา',
      vi: 'Xem các nghiên cứu điển hình'
    },
    hideCaseStudies: {
      en: 'Hide Case Studies',
      ja: 'ケーススタディを非表示',
      th: 'ซ่อนกรณีศึกษา',
      vi: 'Ẩn các nghiên cứu điển hình'
    },
    startYourCampaign: {
      en: 'Start Your Campaign',
      ja: 'キャンペーンを開始',
      th: '',
      vi: 'Triển khai chiến dịch của bạn'
    },
    // Service features
    baiduSEM: {
      en: 'Baidu SEM & Display Advertising',
      ja: 'Baidu SEM＆ディスプレイ広告',
      th: 'Baidu SEM และการโฆษณาแบบแสดงผล',
      vi: 'Baidu SEM & Quảng cáo hiển thị'
    },
    mobileSearchOpt: {
      en: 'Mobile search optimization',
      ja: 'モバイル検索最適化',
      th: 'การเพิ่มประสิทธิภาพการค้นหาบนมือถือ',
      vi: "Tối ưu tìm kiếm trên di động"
    },
    culturalAdaptation: {
      en: 'Cultural adaptation & localization',
      ja: '文化適応＆ローカライゼーション',
      th: 'การปรับทางวัฒนธรรมและการทำให้เหมาะกับท้องถิ่น',
      vi: "Điều chỉnh theo văn hóa & bản địa hóa nội dung"
    },
    mobileFirstDesign: {
      en: 'Mobile-first responsive design',
      ja: 'モバイルファーストレスポンシブデザイン',
      th: 'การออกแบบที่ตอบสนองแบบมือถือเป็นหลัก',
      vi: "Thiết kế tối ưu cho di động"
    },
    wechatOptimization: {
      en: 'WeChat & Chinese browser optimization',
      ja: 'WeChat＆中国ブラウザ最適化',
      th: 'การเพิ่มประสิทธิภาพ WeChat และเบราว์เซอร์จีน',
      vi: 'Tối ưu WeChat & trình duyệt Trung Quốc'
    },
    abTesting: {
      en: 'A/B testing & conversion optimization',
      ja: 'A/Bテスト＆コンバージョン最適化',
      th: 'การทดสอบ A/B และการเพิ่มประสิทธิภาพการแปลง',
      vi: "Thử nghiệm A/B và tối ưu hóa tỷ lệ chuyển đổi"
    },
    baiduSEO: {
      en: 'Baidu SEO & algorithm optimization',
      ja: 'Baidu SEO＆アルゴリズム最適化',
      th: 'Baidu SEO และการเพิ่มประสิทธิภาพอัลกอริทึม',
      vi: 'Baidu SEO & tối ưu thuật toán'
    },
    chineseKeyword: {
      en: 'Chinese keyword research & mapping',
      ja: '中国語キーワードリサーチ＆マッピング',
      th: 'การวิจัยและการทำแผนที่คำหลักภาษาจีน',
      vi: 'Nghiên cứu & lập bản đồ từ khóa tiếng Trung'
    },
    localSEO: {
      en: 'Local SEO for Chinese consumers',
      ja: '中国消費者向けローカルSEO',
      th: 'SEO ท้องถิ่นสำหรับผู้บริโภคชาวจีน',
      vi: "Tối ưu hóa tìm kiếm địa phương cho khách hàng Trung Quốc"
    },
    technicalSEO: {
      en: 'Technical SEO & site performance',
      ja: 'テクニカルSEO＆サイトパフォーマンス',
      th: 'SEO เชิงเทคนิคและประสิทธิภาพเว็บไซต์',
      vi: "ối ưu SEO kỹ thuật và hiệu năng trang web"
    },
    // PR Section
    mediaNetworkAccess: {
      en: 'Media Network Access – Extensive connections across Chinese traditional and digital media outlets',
      ja: 'メディアネットワークアクセス ― 中国の伝統メディアとデジタルメディアに広がる強力なネットワーク',
      th: 'การเข้าถึงเครือข่ายสื่อ - การเชื่อมต่อที่กว้างขวางกับสื่อดั้งเดิมและดิจิทัลของจีน',
      vi: 'Tiếp cận mạng lưới truyền thông - Kết nối rộng rãi với các phương tiện truyền thông truyền thống và kỹ thuật số của Trung Quốc'
    },
    strategicMessaging: {
      en: 'Strategic Messaging – Culturally adapted PR strategies and messaging for Chinese audiences',
      ja: '戦略的メッセージング ― 文化に根差したPR戦略と効果的な発信',
      th: 'การส่งสารเชิงกลยุทธ์ - กลยุทธ์ประชาสัมพันธ์และการส่งสารที่ปรับให้เหมาะกับวัฒนธรรมสำหรับผู้ชมชาวจีน',
      vi: "Thông điệp chiến lược – Xây dựng thông điệp và chiến lược PR thích ứng văn hóa cho thị trường Trung Quốc"
    },
    thoughtLeadership: {
      en: 'Thought Leadership – Position your brand as an industry authority in Chinese markets',
      ja: 'ソートリーダーシップ ― 中国市場でブランドを業界のリーダーとして確立',
      th: 'ความเป็นผู้นำทางความคิด - วางตำแหน่งแบรนด์ของคุณให้เป็นผู้นำอุตสาหกรรมในตลาดจีน',
      vi: "Tư tưởng dẫn đầu - Định vị thương hiệu của bạn như một nhà tiên phong trong ngành tại thị trường Trung Quốc"
    },
    // Service Process Steps
    processStep1Title: {
      en: 'Account Setup & Verification',
      ja: 'アカウント設定・認証',
      th: 'การตั้งค่าและยืนยันบัญชี',
      vi: 'Thiết lập & Xác minh tài khoản'
    },
    processStep1Desc: {
      en: 'We create and certify your Baidu Ads account.',
      ja: 'Baidu広告アカウントを作成し、認証',
      th: 'เราสร้างและรับรองบัญชีโฆษณา Baidu ของคุณ',
      vi: "Chúng tôi tạo và xác thực tài khoản Baidu Ads của bạn"
    },
    processStep2Title: {
      en: 'Strategy Planning',
      ja: '戦略計画',
      th: 'การวางแผนกลยุทธ์',
      vi: 'Lập kế hoạch chiến lược'
    },
    processStep2Desc: {
      en: 'We identify keywords, target audience, and ad structure.',
      ja: 'キーワードやターゲット層、広告構造を戦略的に特定',
      th: 'เราระบุคำหลัก กลุ่มเป้าหมาย และโครงสร้างโฆษณา',
      vi: 'Chúng tôi xác định từ khóa, đối tượng mục tiêu và cấu trúc quảng cáo'
    },
    processStep3Title: {
      en: 'Landing Page Creation',
      ja: 'ランディングページ作成',
      th: 'การสร้างหน้าแรก',
      vi: 'Tạo trang đích'
    },
    processStep3Desc: {
      en: 'We design and host pages in China for instant load times.',
      ja: '中国でのデザインとホスティングにより、スピーディなページを表示',
      th: 'เราออกแบบและโฮสต์หน้าเว็บในจีนเพื่อการโหลดที่รวดเร็ว',
      vi: 'Chúng tôi thiết kế và lưu trữ trang tại Trung Quốc để tải nhanh chóng'
    },
    processStep4Title: {
      en: 'Campaign Launch & Optimization',
      ja: 'キャンペーンの立ち上げと最適化',
      th: 'การเริ่มและเพิ่มประสิทธิภาพแคมเปญ',
      vi: 'Khởi chạy & Tối ưu chiến dịch'
    },
    processStep4Desc: {
      en: 'We monitor performance, adjust bids, and refine targeting.',
      ja: 'パフォーマンスを継続的に監視し、入札とターゲティングを最適化',
      th: 'เราติดตามประสิทธิภาพ ปรับการเสนอราคา และปรับปรุงการกำหนดเป้าหมาย',
      vi: 'Chúng tôi giám sát hiệu suất, điều chỉnh giá thầu và tinh chỉnh mục tiêu'
    },
    processStep5Title: {
      en: 'Performance Review & Next Steps',
      ja: 'パフォーマンスレビューと改善プラン',
      th: 'การตรวจสอบประสิทธิภาพและขั้นตอนถัดไป',
      vi: 'Đánh giá hiệu suất & Các bước tiếp theo'
    },
    processStep5Desc: {
      en: 'Data analysis to improve future campaigns.',
      ja: '将来のキャンペーンを改善するためのデータ分析',
      th: 'การวิเคราะห์ข้อมูลเพื่อปรับปรุงแคมเปญในอนาคต',
      vi: 'Phân tích dữ liệu để cải thiện các chiến dịch trong tương lai'
    },
    // Dubai Real Estate Case Study
    dubaiRealEstateTitle: {
      en: 'Case Study 1: Dubai Real Estate',
      ja: 'ケーススタディ1：ドバイ不動産',
      th: 'กรณีศึกษาที่ 1: อสังหาริมทรัพย์ดูไบ',
      vi: 'Nghiên cứu điển hình 1: Bất động sản Dubai'
    },
    backgroundTitle: {
      en: 'Background',
      ja: '背景',
      th: 'ความเป็นมา',
      vi: 'Bối cảnh'
    },
    solutionTitle: {
      en: 'Our Solution',
      ja: 'ソリューション',
      th: 'โซลูชันของเรา',
      vi: 'Giải pháp của chúng tôi'
    },
    impactTitle: {
      en: 'Impact Highlights',
      ja: '成果・インパクト',
      th: 'ไฮไลต์ผลกระทบ',
      vi: 'Kết quả nổi bật'
    },
    dubaiBackground: {
      en: 'The client faced a significant challenge in reaching and converting Chinese users due to the absence of a localized Chinese-language landing page on their website. Additionally, their website was hosted on overseas servers, leading to slow loading speeds and a poor user experience for visitors in China.',
      ja: 'クライアントは、ウェブサイトにローカライズされた中国語ランディングページがないため、中国のユーザーにリーチし、コンバージョンすることに大きな課題を抱えていました。さらに、ウェブサイトは海外のサーバーでホストされており、中国の訪問者にとって読み込み速度が遅く、ユーザーエクスペリエンスが悪い状態でした。',
      th: 'ลูกค้าเผชิญกับความท้าทายที่สำคัญในการเข้าถึงและแปลงผู้ใช้ชาวจีนเนื่องจากไม่มีหน้าแลนดิ้งภาษาจีนที่ปรับให้เหมาะกับท้องถิ่นบนเว็บไซต์ของพวกเขา นอกจากนี้ เว็บไซต์ของพวกเขายังโฮสต์บนเซิร์ฟเวอร์ต่างประเทศ ทำให้ความเร็วในการโหลดช้าและประสบการณ์ผู้ใช้ไม่ดีสำหรับผู้เยี่ยมชมในประเทศจีน',
      vi: 'Khách hàng đối mặt với thách thức đáng kể trong việc tiếp cận và chuyển đổi người dùng Trung Quốc do thiếu trang đích bằng tiếng Trung được bản địa hóa trên trang web của họ. Ngoài ra, trang web của họ được lưu trữ trên máy chủ ở nước ngoài, dẫn đến tốc độ tải chậm và trải nghiệm người dùng kém cho khách truy cập ở Trung Quốc.'
    },
    dubaiSolution1: {
      en: 'We assisted the client in launching Baidu advertising without requiring a mainland China business entity.',
      ja: '中国本土のビジネスエンティティを必要とせずに、クライアントのBaidu広告の立ち上げを支援しました。',
      th: 'เราช่วยลูกค้าในการเปิดตัวโฆษณา Baidu โดยไม่ต้องมีนิติบุคคลธุรกิจในจีนแผ่นดินใหญ่',
      vi: 'Chúng tôi hỗ trợ khách hàng ra mắt quảng cáo Baidu mà không cần có thực thể kinh doanh tại Trung Quốc đại lục.'
    },
    dubaiSolution2: {
      en: 'We designed and built customized PC and mobile-friendly landing pages hosted within China to ensure fast and stable access for Chinese users.',
      ja: '中国のユーザーが高速で安定したアクセスができるよう、中国国内でホストされたカスタマイズされたPCとモバイル対応のランディングページを設計・構築しました。',
      th: 'เราออกแบบและสร้างหน้าแลนดิ้งที่ปรับแต่งสำหรับ PC และมือถือที่โฮสต์ภายในประเทศจีนเพื่อให้มั่นใจในการเข้าถึงที่รวดเร็วและเสถียรสำหรับผู้ใช้ชาวจีน',
      vi: 'Chúng tôi thiết kế và xây dựng các trang đích tùy chỉnh thân thiện với PC và di động được lưu trữ trong Trung Quốc để đảm bảo truy cập nhanh và ổn định cho người dùng Trung Quốc.'
    },
    dubaiSolution3: {
      en: 'Dual Advertising Channels',
      ja: 'デュアル広告チャネル',
      th: 'ช่องทางโฆษณาคู่',
      vi: 'Kênh quảng cáo kép'
    },
    dubaiImpact1: {
      en: 'High-Quality Leads Generated: Over 200 valid leads were collected via forms and WeChat integration.',
      ja: '高品質リードの生成：フォームとWeChatの統合を通じて200以上の有効なリードを収集しました。',
      th: 'สร้างลีดคุณภาพสูง: รวบรวมลีดที่ถูกต้องมากกว่า 200 รายการผ่านแบบฟอร์มและการรวม WeChat',
      vi: 'Tạo ra khách hàng tiềm năng chất lượng cao: Thu thập được hơn 200 khách hàng tiềm năng hợp lệ thông qua biểu mẫu và tích hợp WeChat.'
    },
    dubaiImpact2: {
      en: 'Performance Growth: CTR (Click-Through Rate) improved significantly, rising from 2.48% to 3.5%.',
      ja: 'パフォーマンスの成長：CTR（クリック率）が2.48%から3.5%に大幅に改善しました。',
      th: 'การเติบโตของประสิทธิภาพ: CTR (อัตราการคลิกผ่าน) ปรับปรุงอย่างมีนัยสำคัญ เพิ่มขึ้นจาก 2.48% เป็น 3.5%',
      vi: 'Tăng trưởng hiệu suất: CTR (Tỷ lệ nhấp chuột) cải thiện đáng kể, tăng từ 2,48% lên 3,5%.'
    },
    dubaiImpact3: {
      en: 'The average cost per lead was well-controlled within 350-500 RMB.',
      ja: '平均リード単価は350-500人民元以内に適切にコントロールされました。',
      th: 'ต้นทุนเฉลี่ยต่อลีดถูกควบคุมได้ดีภายใน 350-500 หยวน',
      vi: 'Chi phí trung bình cho mỗi khách hàng tiềm năng được kiểm soát tốt trong khoảng 350-500 RMB.'
    },
    privateUniversityTitle: {
      en: 'Case Study 2: Private University',
      ja: 'ケーススタディ2：私立大学',
      th: 'กรณีศึกษาที่ 2: มหาวิทยาลัยเอกชน',
      vi: 'Nghiên cứu điển hình 2: Đại học tư thục'
    },
    americanSchoolTitle: {
      en: 'Case Study 1: Private University in Singapore',
      ja: 'ケーススタディ1：シンガポールの私立大学',
      th: 'กรณีศึกษาที่ 1: มหาวิทยาลัยเอกชนในสิงคโปร์',
      vi: 'Nghiên cứu điển hình 1: Đại học tư thục tại Singapore'
    },
    shrewsburySchoolTitle: {
      en: 'Case Study 2: Private University in Italy',
      ja: 'ケーススタディ2：イタリアの私立大学',
      th: 'กรณีศึกษาที่ 2: มหาวิทยาลัยเอกชนในอิตาลี',
      vi: 'Nghiên cứu điển hình 2: Đại học tư thục tại Ý'
    },
  },
  // About Us page
  about: {
    heroTitle: {
      en: 'Your Partner in Reaching ',
      ja: 'GEO(AI)マーケティングから、中華圏・ASEAN市場での事業拡大まで、',
      th: 'พันธมิตรของคุณในการเข้าถึง',
      vi: 'Đối tác của bạn để tiếp cận'
    },
    heroTitleHighlight: {
      en: 'Chinese-Speaking Customers',
      ja: '価値を創出するパートナー',
      th: 'ลูกค้าที่พูดภาษาจีน',
      vi: 'Khách hàng\nnói tiếng Trung'
    },
    heroDescription: {
      en: 'Helping businesses attract, engage, and convert Chinese-speaking customers through integrated online and offline marketing solutions.',
      ja: '',
      th: 'ช่วยธุรกิจดึงดูด สร้างความสัมพันธ์ และแปลงลูกค้าที่พูดภาษาจีนผ่านโซลูชันการตลาดแบบบูรณาการทั้งออนไลน์และออฟไลน์',
      vi: "Đồng hành cùng doanh nghiệp trong việc thu hút, kết nối và chuyển đổi khách hàng nói tiếng Trung bằng các giải pháp marketing toàn diện, kết hợp online và offline."
    },
    ourStory: {
      en: 'About Us',
      ja: '私たちについて',
      th: 'เกี่ยวกับเรา',
      vi: 'Về chúng tôi'
    },
    ourStoryPara1: {
      en: 'We are a locally-based international marketing solutions company with offices in Thailand, Vietnam, Japan, and China.',
      ja: '日本、タイ、ベトナム、中国にオフィスを展開し、地域密着型でグローバルなマーケティング支援を行っています。',
      th: 'เราเป็นบริษัทโซลูชันการตลาดระหว่างประเทศที่ตั้งอยู่ในท้องถิ่น มีสำนักงานในประเทศไทย เวียดนาม ญี่ปุ่น และจีน',
      vi: "Chúng tôi là công ty cung cấp giải pháp marketing quốc tế, hoạt động tại địa phương với văn phòng ở Thái Lan, Việt Nam, Nhật Bản và Trung Quốc."
    },
    ourStoryPara2: {
      en: 'Our expertise lies in Chinese language marketing, creating strategies that combine online reach with offline impact.',
      ja: '中華圏・アセアンマーケティングの専門知識を活かし、オンラインのリーチとオフラインのインパクトを融合させた戦略を実現します。',
      th: 'ความเชี่ยวชาญของเราอยู่ที่การตลาดภาษาจีน สร้างกลยุทธ์ที่ผสมผสานการเข้าถึงออนไลน์กับผลกระทบออฟไลน์',
      vi: "Chuyên môn của chúng tôi tập trung vào marketing tiếng Trung, phát triển chiến lược kết hợp giữa tầm ảnh hưởng online và hiệu quả offline."
    },
    ourStoryPara3: {
      en: 'Through a blend of social media outreach, influencer partnerships, Baidu search optimisation, and in-store marketing, we help brands connect with Chinese-speaking customers, drive measurable results, and build lasting loyalty.',
      ja: 'SNS発信、インフルエンサー連携、Baidu検索最適化、オフラインマーケティングを融合し、ブランドが中華圏顧客とつながり、成果の見える化と持続的なロイヤルティ構築を実現します。',
      th: 'ผ่านการผสมผสานการเข้าถึงโซเชียลมีเดีย ความร่วมมือกับอินฟลูเอนเซอร์ การเพิ่มประสิทธิภาพการค้นหา Baidu และการตลาดในร้าน เราช่วยแบรนด์เชื่อมต่อกับลูกค้าที่พูดภาษาจีน ขับเคลื่อนผลลัพธ์ที่วัดได้ และสร้างความภักดีที่ยั่งยืน',
      vi: "Thông qua sự kết hợp giữa truyền thông mạng xã hội, hợp tác với người có sức ảnh hưởng, tối ưu tìm kiếm trên Baidu và marketing tại cửa hàng, chúng tôi giúp thương hiệu kết nối với khách hàng nói tiếng Trung, tạo ra kết quả đo lường được và xây dựng lòng trung thành bền vững."
    },
    ourStoryPara4: {
      en: 'Our multinational team from Japan, Greater China, Thailand, and Vietnam brings deep cultural insights and industry expertise. This allows us to craft campaigns and experiences that resonate with target audiences and deliver both immediate sales impact and long-term brand value.',
      ja: '日本、中華圏、タイ、ベトナムで構成された多国籍チームが、深い文化的洞察と業界知識を活かします。これにより、ターゲットに響くキャンペーンと体験を創出し、短期的な売上効果と長期的なブランド価値を同時に実現します。',
      th: 'ทีมนานาชาติของเราจากญี่ปุ่น จีนแผ่นดินใหญ่ ไทย และเวียดนาม นำความเข้าใจเชิงลึกทางวัฒนธรรมและความเชี่ยวชาญในอุตสาหกรรม ทำให้เราสามารถสร้างแคมเปญและประสบการณ์ที่โดนใจกลุ่มเป้าหมายและส่งมอบทั้งผลกระทบยอดขายทันทีและคุณค่าแบรนด์ระยะยาว',
      vi: "Với đội ngũ quốc tế đến từ Nhật Bản, Trung Quốc, Thái Lan và Việt Nam, chúng tôi sở hữu sự am hiểu văn hóa sâu sắc và chuyên môn ngành vững chắc. Nhờ đó, chúng tôi tạo nên các chiến dịch và trải nghiệm chạm đến đúng đối tượng khách hàng, vừa mang lại hiệu quả bán hàng tức thì vừa gia tăng giá trị thương hiệu lâu dài."
    },
    stat1Number: {
      en: '95%',
      ja: '95%',
      th: '95%',
      vi: '95%'
    },
    stat1Label: {
      en: 'Client Satisfaction Rate',
      ja: 'クライアント満足度',
      th: 'อัตราความพึงพอใจของลูกค้า',
      vi: 'Tỷ lệ hài lòng của khách hàng'
    },
    stat2Number: {
      en: '75%',
      ja: '75%',
      th: '75%',
      vi: '75%'
    },
    stat2Label: {
      en: 'Referral Rate from Existing Clients',
      ja: '既存クライアントからの紹介率',
      th: 'อัตราการแนะนำจากลูกค้าเดิม',
      vi: 'Tỷ lệ giới thiệu từ khách hàng\nhiện tại'
    },
    stat3Number: {
      en: '100+',
      ja: '100+',
      th: '100+',
      vi: '100+'
    },
    stat3Label: {
      en: 'Brands Supported',
      ja: '支援したブランド',
      th: 'แบรนด์ที่สนับสนุน',
      vi: "Thương hiệu đã hợp tác"
    },
    stat4Number: {
      en: '9,000+',
      ja: '9,000+',
      th: '9,000+',
      vi: '9,000+'
    },
    stat4Label: {
      en: 'Original Chinese Social Media Posts Created',
      ja: 'SNSコンテンツ制作数',
      th: 'โพสต์โซเชียลมีเดียภาษาจีนที่สร้างขึ้น',
      vi: "Bài đăng mạng xã hội tiếng Trung đã tạo"
    },
    ourMission: {
      en: 'Mission',
      ja: 'ミッション',
      th: 'ภารกิจ',
      vi: 'Sứ mệnh'
    },
    ourMissionText: {
      en: 'Shaping a future of living with joy and hope, together!',
      ja: '生きることが楽しい世界を！',
      th: 'ร่วมกันสร้างสรรค์อนาคตแห่งการใช้ชีวิตด้วยความสุขและความหวัง!',
      vi: 'Cùng nhau tạo dựng tương lai tràn đầy niềm vui và hy vọng!'
    },
    ourVision: {
      en: 'Vision',
      ja: 'ビジョン',
      th: 'วิสัยทัศน์',
      vi: 'Tầm nhìn'
    },
    ourVisionText: {
      en: 'Continuously creating unique memories and experiences that bring happiness to people around the world.\n\nExpand our impact through valuable business, striving for growth in harmony with society.',
      ja: '今までにない思い出や体験を創造し、世界中の人々が心の幸福を感じる機会を創りづづける。\n\n価値のある事業を通じて、影響の輪を広げ、社会との調和ある成長を目指す。',
      th: 'สร้างความทรงจำและประสบการณ์ที่เป็นเอกลักษณ์อย่างต่อเนื่องที่นำความสุขมาสู่ผู้คนทั่วโลก\n\nขยายอิทธิพลของเราผ่านธุรกิจที่มีคุณค่า มุ่งสู่การเติบโตที่กลมกลืนกับสังคม',
      vi: "Liên tục tạo ra những kỷ niệm và trải nghiệm độc đáo mang lại hạnh phúc cho mọi người trên thế giới.\n\nMở rộng tầm ảnh hưởng thông qua những hoạt động kinh doanh giá trị, hướng tới sự phát triển hài hòa cùng xã hội."
    },
    ourValue: {
      en: 'Value',
      ja: 'バリュー',
      th: 'คุณค่า',
      vi: 'Giá trị'
    },
    ourValueText: {
      en: 'Act swiftly in the interest of our customers.\n\nBe committed to doing what\'s right, with humility and sincerity.\n\nChallenge with creativity and passion, never giving up until success is achieved.',
      ja: 'クライアントのために迅速に行動する。\n\n正しさに拘り、謙虚かつ誠実に生きる。\n\n創造性と熱意を持ち、できるまでやり続ける。',
      th: 'ดำเนินการอย่างรวดเร็วเพื่อผลประโยชน์ของลูกค้า\n\nมุ่งมั่นทำสิ่งที่ถูกต้องด้วยความถ่อมตนและความจริงใจ\n\nท้าทายด้วยความคิดสร้างสรรค์และความหลงใหล ไม่ยอมแพ้จนกว่าจะประสบความสำเร็จ',
      vi: 'Hành động nhanh chóng vì lợi ích của khách hàng.\n\nCam kết làm điều đúng đắn với sự khiêm tốn và chân thành.\n\nThử thách với sáng tạo và đam mê, không bao giờ bỏ cuộc cho đến khi đạt được thành công.'
    },
    founderMessage: {
      en: 'Message from the Founder',
      ja: '代表者メッセージ',
      th: 'ข้อความจากผู้ก่อตั้ง',
      vi: 'Thông điệp từ Nhà sáng lập'
    },
    founderMessagePara1: {
      en: 'Through my experience working across borders, I have realized one simple truth: every country has its own passion, creativity, and craftsmanship — yet much of that beauty remains unseen beyond its borders.',
      ja: '海外での経験を通じ、私は一つの共通点に気づきました。それは、どの国にも素晴らしい「想い」と「ものづくり」があるにもかかわらず、その魅力が国境を越えて十分に伝わっていないということです。',
      th: 'Through my experience working across borders, I have realized one simple truth: every country has its own passion, creativity, and craftsmanship — yet much of that beauty remains unseen beyond its borders.',
      vi: 'Through my experience working across borders, I have realized one simple truth: every country has its own passion, creativity, and craftsmanship — yet much of that beauty remains unseen beyond its borders.'
    },
    founderMessagePara2: {
      en: 'Japan\'s sincerity and precision, Thailand\'s warmth and sensitivity, Vietnam\'s energy and determination. Across Asia, each culture holds its own strength and charm rooted in its people and traditions.',
      ja: '日本の誠実さと細やかさ、タイの温かさと感性、ベトナムの情熱と勢い。アジアには、それぞれの文化に根ざした多様な魅力があります。',
      th: 'Japan\'s sincerity and precision, Thailand\'s warmth and sensitivity, Vietnam\'s energy and determination. Across Asia, each culture holds its own strength and charm rooted in its people and traditions.',
      vi: 'Japan\'s sincerity and precision, Thailand\'s warmth and sensitivity, Vietnam\'s energy and determination. Across Asia, each culture holds its own strength and charm rooted in its people and traditions.'
    },
    founderMessagePara3: {
      en: 'Today, as international exchange becomes more limited, it is more important than ever to share the true value of products and services, and to reconnect people and nations through meaningful communication.',
      ja: '国際交流が停滞しがちな今だからこそ、価値ある商品やサービスの魅力を発信し、人と人、国と国を再びつなぐことが必要です。',
      th: 'Today, as international exchange becomes more limited, it is more important than ever to share the true value of products and services, and to reconnect people and nations through meaningful communication.',
      vi: 'Today, as international exchange becomes more limited, it is more important than ever to share the true value of products and services, and to reconnect people and nations through meaningful communication.'
    },
    founderMessagePara4: {
      en: 'Z Creative Partners was born from this belief. We work alongside companies from Japan, Thailand, and Vietnam, transforming what we learn locally into marketing solutions that communicate each brand\'s value to Chinese-speaking audiences and to the world.',
      ja: 'Z Creative Partnersは、そうした想いから生まれました。日本・タイ・ベトナムの企業と共に歩み、それぞれの地域で培った経験をもとに、中華圏をはじめとする世界の人々へ価値を届けるマーケティング支援を行っています。',
      th: 'Z Creative Partners was born from this belief. We work alongside companies from Japan, Thailand, and Vietnam, transforming what we learn locally into marketing solutions that communicate each brand\'s value to Chinese-speaking audiences and to the world.',
      vi: 'Z Creative Partners was born from this belief. We work alongside companies from Japan, Thailand, and Vietnam, transforming what we learn locally into marketing solutions that communicate each brand\'s value to Chinese-speaking audiences and to the world.'
    },
    founderMessagePara5: {
      en: 'Our team is built on diversity — different languages, cultures, and perspectives — and we move quickly to keep pace with the changing world. By embracing diversity and combining our strengths, we strive to connect the unique values of Asia with the global stage.',
      ja: '私たちは、多様な文化背景を持つチームで協働し、世界の動きに合わせて、スピーディーに事業を進めています。多様性を尊重し、それぞれの感性と強みを活かしながら、アジアの価値を世界へとつなぐ。それが、Z Creative Partnersの使命です。',
      th: 'Our team is built on diversity — different languages, cultures, and perspectives — and we move quickly to keep pace with the changing world. By embracing diversity and combining our strengths, we strive to connect the unique values of Asia with the global stage.',
      vi: 'Our team is built on diversity — different languages, cultures, and perspectives — and we move quickly to keep pace with the changing world. By embracing diversity and combining our strengths, we strive to connect the unique values of Asia with the global stage.'
    },
    founderMessagePara6: {
      en: 'This is the mission that drives us. Our mission is "Shaping a future of living with joy and hope, together." Through this mission, we hope that people everywhere will discover the richness of the world around them, find moments of joy and inspiration in everyday life, and once again feel the simple happiness of being alive. We believe that these small moments of connection can bring people and cultures closer together, and make the world a more vibrant and meaningful place.',
      ja: '私たちのミッションは、「生きることが楽しい世界を」実現することです。私たちはこのミッションを通じて、世界のさまざまな文化や価値観に触れながら、一人ひとりが「この世界っていいな」と感じられる瞬間を増やしていきたいと願っています。その小さな感動の積み重ねが、人と人、国と国をつなぎ、より豊かな世界をつくると信じています。',
      th: 'This is the mission that drives us. Our mission is "Shaping a future of living with joy and hope, together." Through this mission, we hope that people everywhere will discover the richness of the world around them, find moments of joy and inspiration in everyday life, and once again feel the simple happiness of being alive. We believe that these small moments of connection can bring people and cultures closer together, and make the world a more vibrant and meaningful place.',
      vi: 'This is the mission that drives us. Our mission is "Shaping a future of living with joy and hope, together." Through this mission, we hope that people everywhere will discover the richness of the world around them, find moments of joy and inspiration in everyday life, and once again feel the simple happiness of being alive. We believe that these small moments of connection can bring people and cultures closer together, and make the world a more vibrant and meaningful place.'
    },
    founderName: {
      en: 'Honami Murayama',
      ja: '株式会社 Z Creative Partners\n ファウンダー　村山穂奈実',
      th: 'โฮนามิ มุระยามะ',
      vi: 'Honami Murayama'
    },
    founderTitle: {
      en: 'Founder, Z Creative Partners Co., Ltd.',
      ja: '',
      th: 'Founder, Z Creative Partners Co., Ltd.',
      vi: 'Founder, Z Creative Partners Co., Ltd.'
    },
    meetOurTeam: {
      en: 'Our Core Members',
      ja: 'コアメンバー',
      th: 'สมาชิกหลักของเรา',
      vi: 'Thành viên cốt lõi của chúng tôi'
    },
    meetOurTeamDesc: {
      en: 'The People Behind Your Success',
      ja: '成果をともに築くチーム',
      th: 'คนที่อยู่เบื้องหลังความสำเร็จของคุณ',
      vi: 'Những người đứng sau thành công của bạn'
    },
    clickToExpand: {
      en: 'Click to expand',
      ja: 'クリックして展開',
      th: 'คลิกเพื่อขยาย',
      vi: 'Nhấp để mở rộng'
    },
    readLess: {
      en: 'Read less',
      ja: '閉じる',
      th: 'อ่านน้อยลง',
      vi: 'Đọc ít hơn'
    },
    readMore: {
      en: 'more',
      ja: '続きを読む',
      th: 'อ่านเพิ่มเติม',
      vi: 'xem thêm'
    },
    about: {
      en: 'About',
      ja: 'アバウト',
      th: 'เกี่ยวกับ',
      vi: 'Về'
    },
    areasOfExpertise: {
      en: 'Areas of Expertise',
      ja: '専門分野',
      th: 'ความเชี่ยวชาญ',
      vi: 'Lĩnh vực chuyên môn'
    },
    connectOnLinkedIn: {
      en: 'Connect on LinkedIn',
      ja: 'LinkedInでつながる',
      th: 'เชื่อมต่อบน LinkedIn',
      vi: 'Kết nối trên LinkedIn'
    },
    scrollToNavigate: {
      en: 'Click arrows or scroll to navigate',
      ja: '矢印をクリックするかスクロールしてナビゲート',
      th: 'คลิกลูกศรหรือเลื่อนเพื่อนำทาง',
      vi: 'Nhấp vào mũi tên hoặc cuộn để điều hướng'
    },
    ourValues: {
      en: 'Our Values',
      ja: '私たちが大切にすること',
      th: 'ค่านิยมของเรา',
      vi: 'Giá trị của chúng tôi'
    },
    ourValuesDesc: {
      en: 'The principles that guide everything we do',
      ja: '私たちの行動指針',
      th: 'หลักการที่นำทางทุกสิ่งที่เราทำ',
      vi: 'Các nguyên tắc hướng dẫn mọi thứ chúng tôi làm'
    },
    culturalBridge: {
      en: 'Cultural Bridge',
      ja: '文化の架け橋',
      th: 'สะพานวัฒนธรรม',
      vi: 'Cầu nối văn hóa'
    },
    culturalBridgeDesc: {
      en: 'We respect and celebrate cultural differences while finding common ground for meaningful connections.',
      ja: '文化の多様性を尊重し大切にしつつ、真のつながりを築くための共通点を見つけます。',
      th: 'เราเคารพและเฉลิมฉลองความแตกต่างทางวัฒนธรรมในขณะที่หาจุดร่วมสำหรับการเชื่อมต่อที่มีความหมาย',
      vi: 'Chúng tôi tôn trọng và tôn vinh sự khác biệt văn hóa trong khi tìm kiếm điểm chung cho những kết nối ý nghĩa'
    },
    innovationFirst: {
      en: 'Innovation First',
      ja: 'イノベーション第一',
      th: 'นวัตกรรมเป็นอันดับแรก',
      vi: 'Đổi mới là ưu tiên'
    },
    innovationFirstDesc: {
      en: 'We continuously explore new technologies and strategies to stay ahead of market trends.',
      ja: '市場のトレンドに先んじるため、新しい技術と戦略を継続的に探求します',
      th: 'เราสำรวจเทคโนโลยีและกลยุทธ์ใหม่ๆ อย่างต่อเนื่องเพื่อนำหน้าแนวโน้มตลาด',
      vi: 'Chúng tôi liên tục khám phá công nghệ và chiến lược mới để đi trước xu hướng thị trường'
    },
    clientSuccess: {
      en: 'Client Success',
      ja: 'クライアントの成功',
      th: 'ความสำเร็จของลูกค้า',
      vi: 'Thành công của khách hàng'
    },
    clientSuccessDesc: {
      en: 'Our success is measured by the growth and achievement of our clients\' business goals.',
      ja: '私たちの成功は、クライアントのビジネス目標の成長と達成によって測定されます',
      th: 'ความสำเร็จของเราวัดจากการเติบโตและความสำเร็จของเป้าหมายทางธุรกิจของลูกค้า',
      vi: 'Thành công của chúng tôi được đo lường bằng sự tăng trưởng và thành tựu trong mục tiêu kinh doanh của khách hàng'
    },
    transparency: {
      en: 'Transparency',
      ja: '透明性',
      th: 'ความโปร่งใส',
      vi: 'Minh bạch'
    },
    transparencyDesc: {
      en: 'We believe in open communication, honest reporting, and clear accountability.',
      ja: 'オープンなコミュニケーション、誠実な報告、明確な説明責任を信じています',
      th: 'เราเชื่อในการสื่อสารที่เปิดเผย การรายงานที่ซื่อสัตย์ และความรับผิดชอบที่ชัดเจน',
      vi: 'Chúng tôi tin vào giao tiếp cởi mở, báo cáo trung thực và trách nhiệm rõ ràng'
    },
    excellence: {
      en: 'Excellence',
      ja: '卓越性',
      th: 'ความเป็นเลิศ',
      vi: 'Xuất sắc'
    },
    excellenceDesc: {
      en: 'We strive for the highest quality in every project, no matter how big or small.',
      ja: 'プロジェクトの大小に関わらず、すべてのプロジェクトで最高品質を目指します',
      th: 'เรามุ่งมั่นเพื่อคุณภาพสูงสุดในทุกโครงการ ไม่ว่าจะใหญ่หรือเล็ก',
      vi: 'Chúng tôi phấn đấu đạt chất lượng cao nhất trong mọi dự án, dù lớn hay nhỏ'
    },
    partnership: {
      en: 'Partnership',
      ja: 'パートナーシップ',
      th: 'ความเป็นหุ้นส่วน',
      vi: 'Đối tác'
    },
    partnershipDesc: {
      en: 'We work as an extension of your team, collaborating closely to achieve shared objectives.',
      ja: 'チームの延長として働き、共通の目標を達成するために密接に協力します',
      th: 'เราทำงานเป็นส่วนขยายของทีมคุณ ร่วมมือกันอย่างใกล้ชิดเพื่อบรรลุวัตถุประสงค์ร่วมกัน',
      vi: 'Chúng tôi làm việc như một phần mở rộng của đội ngũ bạn, hợp tác chặt chẽ để đạt được mục tiêu chung'
    },
    // Company Overview Section
    companyOverview: {
      en: 'Company Overview',
      ja: '会社概要',
      th: 'ภาพรวมบริษัท',
      vi: 'Tổng quan công ty'
    },
    companyNameLabel: {
      en: 'Company Name',
      ja: '会社名',
      th: 'ชื่อบริษัท',
      vi: 'Tên công ty'
    },
    companyName: {
      en: 'Z Creative Partners Co., Ltd.',
      ja: '株式会社 Z Creative Partners',
      th: 'Z Creative Partners Co., Ltd.',
      vi: 'Z Creative Partners Co., Ltd.'
    },
    representativeLabel: {
      en: 'Representative',
      ja: '代表取締役',
      th: 'ผู้แทน',
      vi: 'Người đại diện'
    },
    representative: {
      en: 'Honami Murayama',
      ja: '村山穂奈実',
      th: 'Honami Murayama',
      vi: 'Honami Murayama'
    },
    paidInCapitalLabel: {
      en: 'Paid-in Capital',
      ja: '資本金',
      th: 'ทุนจดทะเบียน',
      vi: 'Vốn điều lệ'
    },
    paidInCapital: {
      en: 'JPY 25,000,000',
      ja: '2,500万円',
      th: 'JPY 25,000,000',
      vi: 'JPY 25,000,000'
    },
    paidInCapitalNote: {
      en: '(Consolidated group total)',
      ja: '(連結グループ合計)',
      th: '(รวมกลุ่มรวม)',
      vi: '(Tổng hợp nhóm)'
    },
    numberOfEmployeesLabel: {
      en: 'Number of Employees',
      ja: '従業員数',
      th: 'จำนวนพนักงาน',
      vi: 'Số lượng nhân viên'
    },
    numberOfEmployees: {
      en: '65',
      ja: '65名',
      th: '65',
      vi: '65'
    },
    numberOfEmployeesNote: {
      en: '(On a group consolidated basis, including outsourced personnel)',
      ja: '(グループ連結ベース、契約社員・業務委託を含む)',
      th: '(บนพื้นฐานการรวมกลุ่ม รวมถึงบุคลากรภายนอก)',
      vi: '(Trên cơ sở hợp nhất nhóm, bao gồm nhân viên gia công ngoài)'
    },
    headOfficeLabel: {
      en: 'Headquarters',
      ja: '本社',
      th: 'สำนักงานใหญ่',
      vi: 'Trụ sở chính'
    },
    headOfficeAddress: {
      en: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo, Japan',
      ja: '東京都港区六本木三丁目16番12号六本木KSビル5F',
      th: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo, Japan',
      vi: 'KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo, Japan'
    },
    globalOfficeLabel: {
      en: 'Global Office',
      ja: 'グローバルオフィス',
      th: 'สำนักงานทั่วโลก',
      vi: 'Văn phòng toàn cầu'
    },
    thailandOffice: {
      en: 'Thailand',
      ja: 'タイ',
      th: 'ไทย',
      vi: 'Thái Lan'
    },
    thailandOfficeAddress: {
      en: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260',
      ja: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260',
      th: '71 อุดมสุข ซอย 46 บางนาเหนือ บางนา กรุงเทพฯ 10260',
      vi: '71 Udom Suk, 46 Alley, Bang Na Nuea, Bang Na, Bangkok 10260'
    },
    vietnamOffice: {
      en: 'Vietnam',
      ja: 'ベトナム',
      th: 'เวียดนาม',
      vi: 'Việt Nam'
    },
    vietnamOfficeAddress: {
      en: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      ja: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      th: 'Sabay Building, No. 5 Dong Nai Street, Ho Chi Minh City',
      vi: 'Toà nhà Sabay, số 5, đường Đồng Nai, Thành phố Hồ Chí Minh'
    },
    chinaOffice: {
      en: 'China',
      ja: '中国',
      th: 'จีน',
      vi: 'Trung Quốc'
    },
    chinaOfficeAddress: {
      en: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      ja: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      th: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province',
      vi: '9F Building 03 Area A, Phase III Software Park, Jimei District, Xiamen City, Fujian Province'
    },
    businessPortfolioLabel: {
      en: 'Business Portfolio',
      ja: '事業内容',
      th: 'พอร์ตโฟลิโอธุรกิจ',
      vi: 'Lĩnh vực kinh doanh'
    },
    businessPortfolioText: {
      en: 'We provide cross-border marketing solutions that seamlessly connect online channels with physical stores and e-commerce through SNS marketing and O2O/OMO strategies tailored for the Greater China and ASEAN markets.',
      ja: 'GEO（生成エンジン最適化）や、中華圏・アセアン市場向けのSNSマーケティングとO2O／OMO施策を通じて、オンラインからオフライン・EC までを一貫してつなぐクロスボーダー型マーケティングソリューションを提供。',
      th: 'เรามอบโซลูชันการทำการตลาดข้ามพรมแดน ที่เชื่อมต่อช่องทางออนไลน์กับหน้าร้านและอีคอมเมิร์ซอย่างครบวงจร ผ่านการทำการตลาดบนโซเชียลมีเดียและกลยุทธ์ O2O/OMO สำหรับตลาดจีนและอาเซียน',
      vi: 'Chúng tôi cung cấp các giải pháp marketing đa thị trường, kết nối liền mạch giữa kênh online với cửa hàng và thương mại điện tử thông qua marketing trên các nền tảng mạng xã hội cùng các chiến lược O2O/OMO, được thiết kế riêng cho thị trường Trung Quốc và Đông Nam Á.'
    },
  },
  // Legal pages - simplified versions for demo
  privacy: {
    title: {
      en: 'Privacy Policy',
      ja: 'プライバシーポリシー',
      th: 'นโยบายความเป็นส่วนตัว',
      vi: 'Chính sách bảo mật'
    },
    lastUpdated: {
      en: 'Last updated: January 2025',
      ja: '最終更新日：2025年1月',
      th: 'อัพเดทล่าสุด: มกราคม 2025',
      vi: 'Cập nhật lần cuối: Tháng 1 năm 2025'
    },
    introduction: {
      en: 'Introduction',
      ja: 'はじめに',
      th: 'บทนำ',
      vi: 'Giới thiệu'
    },
    introText: {
      en: 'Z Creative Partners Co., Ltd. ("we," "our," or "us") respects your privacy and is committed to protecting your personal data.',
      ja: 'Z Creative Partners Co., Ltd.（「当社」、「私たち」）は、お客様のプライバシーを尊重し、個人データの保護に努めています。',
      th: 'Z Creative Partners Co., Ltd. ("เรา" หรือ "ของเรา") เคารพความเป็นส่วนตัวของคุณและมุ่งมั่นในการปกป้องข้อมูลส่วนบุคคลของคุณ',
      vi: 'Z Creative Partners Co., Ltd. ("chúng tôi", "của chúng tôi") tôn trọng quyền riêng tư của bạn và cam kết bảo vệ dữ liệu cá nhân của bạn.'
    }
  },
  terms: {
    title: {
      en: 'Terms of Use',
      ja: '利用規約',
      th: 'ข้อกำหนดการใช้งาน',
      vi: 'Điều khoản sử dụng'
    },
    lastUpdated: {
      en: 'Last updated: January 2025',
      ja: '最終更新日：2025年1月',
      th: 'อัพเดทล่าสุด: มกราคม 2025',
      vi: 'Cập nhật lần cuối: Tháng 1 năm 2025'
    },
    agreement: {
      en: 'Agreement to Terms',
      ja: '利用規約への同意',
      th: 'ข้อตกลงและเงื่อนไข',
      vi: 'Thỏa thuận về điều khoản'
    },
    agreementText: {
      en: 'By accessing and using this website, you accept and agree to be bound by the terms and provisions of this agreement.',
      ja: '本ウェブサイトにアクセスしご利用いただくことで、本契約の条件および規定に同意したものとみなされます。',
      th: 'โดยการเข้าถึงและใช้เว็บไซต์นี้ คุณยอมรับและตกลงที่จะผูกพันโดยข้อกำหนดและบทบัญญัติของข้อตกลงนี้',
      vi: 'Bằng việc truy cập và sử dụng trang web này, bạn chấp nhận và đồng ý bị ràng buộc bởi các điều khoản và quy định của thỏa thuận này.'
    }
  },
  cookies: {
    title: {
      en: 'Cookie Policy',
      ja: 'Cookieポリシー',
      th: 'นโยบายคุกกี้',
      vi: 'Chính sách Cookie'
    },
    updatedAt: {
      en: 'Updated on 2025-08-09',
      ja: '更新日：2025年8月9日',
      th: 'อัพเดทเมื่อ 2025-08-09',
      vi: 'Cập nhật ngày 09-08-2025'
    },
    definitions: {
      en: 'Definitions and Key Terms',
      ja: '定義と主要用語',
      th: 'คำนิยามและคำสำคัญ',
      vi: 'Định nghĩa và thuật ngữ chính'
    },
    definitionsText: {
      en: 'To help explain things as clearly as possible in this Cookie Policy, these definitions apply every time any of these terms are referenced.',
      ja: 'このCookieポリシーでできるだけ明確に説明するため、これらの用語が参照されるたびに、これらの定義が適用されます。',
      th: 'เพื่อช่วยอธิบายสิ่งต่าง ๆ ให้ชัดเจนที่สุดในนโยบายคุกกี้นี้ คำนิยามเหล่านี้จะใช้ทุกครั้งที่มีการอ้างอิงคำเหล่านี้',
      vi: 'Để giúp giải thích mọi thứ rõ ràng nhất có thể trong Chính sách Cookie này, các định nghĩa này áp dụng mỗi khi bất kỳ thuật ngữ nào được tham chiếu.'
    }
  },
  // Social media links for each language/country
  socialMediaLinks: {
    instagram: {
      en: 'https://www.instagram.com/zcreativepartners/',
      ja: 'https://www.instagram.com/zcreativepartners/', // Replace with Japan-specific Instagram
      th: 'https://www.instagram.com/zcreativepartners/', // Replace with Thailand-specific Instagram
      vi: 'https://www.instagram.com/zcreativepartners/' // Replace with Vietnam-specific Instagram
    },
    facebook: {
      en: 'https://www.facebook.com/profile.php?id=61575201952613',
      ja: 'https://www.facebook.com/profile.php?id=61575201952613', // Replace with Japan-specific Facebook
      th: 'https://www.facebook.com/profile.php?id=61575201952613', // Replace with Thailand-specific Facebook
      vi: 'https://www.facebook.com/ZCreativePartnersVietNam' // Replace with Vietnam-specific Facebook
    }
  },
  // News & Market Insights page
  marketInsights: {
    heroTitle: {
      en: 'Trends, Strategies, and Data for ',
      ja: '中華圏・アセアン市場で勝つための',
      th: 'แนวโน้ม กลยุทธ์ และข้อมูลสำหรับ',
      vi: "Xu hướng, Chiến lược và Dữ liệu để "
    },
    heroHighlight: {
      en: 'Winning in the Chinese-Speaking Market',
      ja: 'トレンド、戦略、データ',
      th: 'การชนะในตลาดที่พูดภาษาจีน',
      vi: "Chinh phục thị trường nói tiếng Trung"
    },
    heroSubtitle: {
      en: 'Stay ahead with in-depth analysis, actionable strategies, and the latest trends shaping Chinese-speaking consumer behavior, social media platforms, and cross-border marketing.',
      ja: '深い分析、実用的な戦略、および中華圏・アセアン消費者の行動、SNSプラットフォーム、越境マーケティングを形作る最新トレンドで一歩先を行きます。',
      th: 'ก้าวนำด้วยการวิเคราะห์เชิงลึก กลยุทธ์ที่นำไปปฏิบัติได้ และแนวโน้มล่าสุดที่กำหนดพฤติกรรมผู้บริโภคที่พูดภาษาจีน แพลตฟอร์มโซเชียลมีเดีย และการตลาดข้ามพรมแดน',
      vi: "Dẫn đầu thị trường với các phân tích chuyên sâu, chiến lược khả thi và xu hướng mới nhất định hình hành vi người tiêu dùng nói tiếng Trung, các nền tảng mạng xã hội và marketing toàn cầu."
    },
    subscribeToReports: {
      en: 'Subscribe to Reports',
      ja: 'レポートを購読',
      th: 'สมัครรับรายงาน',
      vi: 'Đăng ký nhận báo cáo'
    },
    articlesTitle: {
      en: 'Articles',
      ja: '記事',
      th: 'บทความ',
      vi: 'Bài viết'
    },
    articlesSubtitle: {
      en: 'Deep dives into market trends, consumer behavior, and strategic insights for reaching Chinese-speaking consumers',
      ja: '中華圏・アセアン消費者にリーチするための市場トレンド、消費者行動、戦略的インサイトへの深い探求',
      th: 'การศึกษาเชิงลึกเกี่ยวกับแนวโน้มตลาด พฤติกรรมผู้บริโภค และข้อมูลเชิงกลยุทธ์สำหรับการเข้าถึงผู้บริโภคชาวจีน',
      vi: 'Phân tích chuyên sâu về xu hướng thị trường, hành vi tiêu dùng và chiến lược tiếp cận\nkhách hàng Trung Quốc'
    },
    industryDataTitle: {
      en: 'Industry Data',
      ja: '業界データ',
      th: 'ข้อมูลอุตสาหกรรม',
      vi: 'Dữ liệu ngành'
    },
    // Industry statistics
    annualTourists: {
      en: 'Annual Chinese Outbound Tourists',
      ja: '年間の中国人海外旅行者数',
      th: 'นักท่องเที่ยวจีนขาออกต่อปี',
      vi: 'Du khách Trung Quốc đi du lịch hàng năm'
    },
    annualTouristsDesc: {
      en: 'Record-high number of Chinese travelers visiting international destinations',
      ja: '海外を訪れる中国人旅行者数が過去最高を記録',
      th: 'จำนวนนักเดินทางชาวจีนที่เดินทางไปจุดหมายปลายทางระหว่างประเทศสูงเป็นประวัติการณ์',
      vi: 'Số lượng kỷ lục du khách Trung Quốc đến các điểm du lịch quốc tế'
    },
    averageSpending: {
      en: 'Average Per Person Spending',
      ja: '一人当たり平均支出',
      th: 'การใช้จ่ายเฉลี่ยต่อคน',
      vi: 'Chi tiêu trung bình mỗi người'
    },
    averageSpendingDesc: {
      en: 'Higher spending power with a focus on premium experiences',
      ja: 'プレミアム体験を重視する高い消費力',
      th: 'กำลังซื้อที่สูงขึ้นโดยเน้นที่ประสบการณ์พรีเมียม',
      vi: 'Sức mua cao hơn với sự tập trung vào trải nghiệm cao cấp'
    },
    wechatPlanning: {
      en: 'Use WeChat for Travel Planning',
      ja: 'WeChatで旅行計画',
      th: 'ใช้ WeChat สำหรับการวางแผนการเดินทาง',
      vi: 'Sử dụng WeChat để lên kế hoạch du lịch'
    },
    wechatPlanningDesc: {
      en: 'WeChat ecosystem dominates travel research and booking',
      ja: 'WeChatエコシステムが旅行リサーチと予約をリード',
      th: 'ระบบนิเวศ WeChat ครอบงำการค้นหาและการจองการเดินทาง',
      vi: 'Hệ sinh thái WeChat chi phối việc nghiên cứu và đặt chỗ du lịch'
    },
    xiaohongshuReviews: {
      en: 'Check Reviews on Xiaohongshu',
      ja: 'Redでレビューを確認',
      th: 'ตรวจสอบรีวิวบน Xiaohongshu',
      vi: 'Kiểm tra đánh giá trên Xiaohongshu'
    },
    xiaohongshuReviewsDesc: {
      en: 'Social proof through peer reviews drives decision-making',
      ja: '口コミ・レビューという裏付けが購買行動を促進',
      th: 'การพิสูจน์ทางสังคมผ่านรีวิวจากเพื่อนขับเคลื่อนการตัดสินใจ',
      vi: 'Bằng chứng xã hội thông qua đánh giá từ đồng nghiệp thúc đẩy quyết định'
    },
    mobileBooking: {
      en: 'Mobile-First Booking Preference',
      ja: 'モバイル優先の予約傾向',
      th: 'ความชื่นชอบการจองผ่านมือถือเป็นหลัก',
      vi: 'Ưu tiên đặt chỗ qua di động'
    },
    localExperiences: {
      en: 'Preference for Authentic Local Experiences',
      ja: 'リアルな現地体験を求める嗜好',
      th: 'ความชื่นชอบประสบการณ์ท้องถิ่นที่แท้จริง',
      vi: "Ưu tiên trải nghiệm địa phương"
    },
    kolInfluence: {
      en: 'Influenced by KOL Recommendations',
      ja: 'KOLの推奨が購買行動に影響',
      th: 'ได้รับอิทธิพลจากคำแนะนำของ KOL',
      vi: 'Bị ảnh hưởng bởi khuyến nghị của KOL'
    }
  },
  // Team members data
  teamMembers: {
    member1Name: {
      en: 'David Chen',
      ja: 'デイビッド・チェン',
      th: 'เดวิด เฉิน',
      vi: 'David Chen'
    },
    member1Position: {
      en: 'CEO & Founder',
      ja: 'CEO & 創業者',
      th: 'CEO และผู้ก่อตั้ง',
      vi: 'CEO & Người sáng lập'
    },
    member1ShortBio: {
      en: '15+ years experience in cross-border marketing',
      ja: '越境マーケティング15年以上の経験',
      th: 'ประสบการณ์กว่า 15 ปีในการตลาดข้ามพรมแดน',
      vi: 'Hơn 15 năm kinh nghiệm trong tiếp thị xuyên biên giới'
    },
    member1FullBio: {
      en: 'David founded Z Creative Partners with a vision to bridge Southeast Asian businesses with Chinese consumers. With over 15 years of experience in cross-border marketing and digital strategy, he has helped hundreds of brands successfully enter and expand in Chinese markets. David holds an MBA from INSEAD and speaks fluent Mandarin, English, and Bahasa Malaysia.',
      ja: 'デイビッドは、東南アジアのビジネスと中華圏の消費者を結びつけるビジョンを持ってZ Creative Partnersを設立しました。越境マーケティングとデジタル戦略で15年以上の経験を持ち、数百のブランドが中国市場への参入と拡大に成功するのを支援してきました。INSEADでMBAを取得し、北京語、英語、マレー語を流暢に話します。',
      th: 'เดวิดก่อตั้ง Z Creative Partners ด้วยวิสัยทัศน์ในการเชื่อมโยงธุรกิจเอเชียตะวันออกเฉียงใต้กับผู้บริโภคชาวจีน ด้วยประสบการณ์กว่า 15 ปีในการตลาดข้ามพรมแดนและกลยุทธ์ดิจิทัล เขาได้ช่วยแบรนด์หลายร้อยแบรนด์ประสบความสำเร็จในการเข้าสู่และขยายตลาดจีน เดวิดจบ MBA จาก INSEAD และพูดภาษาจีนกลาง อังกฤษ และมาเลย์ได้อย่างคล่องแคล่ว',
      vi: 'David thành lập Z Creative Partners với tầm nhìn kết nối các doanh nghiệp Đông Nam Á với người tiêu dùng Trung Quốc. Với hơn 15 năm kinh nghiệm trong tiếp thị xuyên biên giới và chiến lược số, ông đã giúp hàng trăm thương hiệu thành công thâm nhập và mở rộng tại thị trường Trung Quốc. David có bằng MBA từ INSEAD và nói thông thạo tiếng Quan Thoại, tiếng Anh và tiếng Mã Lai.'
    },
    member1Expertise1: {
      en: 'Cross-border Marketing',
      ja: '越境マーケティング',
      th: 'การตลาดข้ามพรมแดน',
      vi: 'Tiếp thị xuyên biên giới'
    },
    member1Expertise2: {
      en: 'Business Strategy',
      ja: 'ビジネス戦略',
      th: 'กลยุทธ์ธุรกิจ',
      vi: 'Chiến lược kinh doanh'
    },
    member1Expertise3: {
      en: 'Chinese Market Entry',
      ja: '中国市場参入',
      th: 'การเข้าสู่ตลาดจีน',
      vi: 'Thâm nhập thị trường Trung Quốc'
    },
    member2Name: {
      en: 'Lisa Wang',
      ja: 'リサ・ワン',
      th: 'ลิซ่า หวัง',
      vi: 'Lisa Wang'
    },
    member2Position: {
      en: 'Chief Marketing Officer',
      ja: 'チーフマーケティングオフィサー',
      th: 'ประธานเจ้าหน้าที่ฝ่ายการตลาด',
      vi: 'Giám đốc Tiếp thị'
    },
    member2ShortBio: {
      en: 'Social media marketing expert with 12+ years experience',
      ja: 'SNSマーケティングのエキスパート、12年以上の経験',
      th: 'ผู้เชี่ยวชาญการตลาดโซเชียลมีเดียประสบการณ์กว่า 12 ปี',
      vi: 'Chuyên gia tiếp thị mạng xã hội với hơn 12 năm kinh nghiệm'
    },
    member2FullBio: {
      en: 'Lisa leads our marketing team with expertise in Chinese social media platforms including WeChat, Weibo, Xiaohongshu, and Douyin. She has managed campaigns for luxury brands, F&B chains, and tourism companies across Southeast Asia. Lisa is a certified Weibo and WeChat marketing specialist and regularly speaks at industry conferences.',
      ja: 'リサは、WeChat、Weibo、Xiaohongshu、Douyinを含む中国のSNSプラットフォームの専門知識を持ってマーケティングチームを率いています。東南アジア全域のラグジュアリーブランド、F&Bチェーン、観光会社のキャンペーンを管理してきました。WeiboとWeChatのマーケティング認定スペシャリストであり、業界会議で定期的に講演しています。',
      th: 'ลิซ่านำทีมการตลาดของเราด้วยความเชี่ยวชาญในแพลตฟอร์มโซเชียลมีเดียจีน รวมถึง WeChat, Weibo, Xiaohongshu และ Douyin เธอได้จัดการแคมเปญสำหรับแบรนด์หรู ร้านอาหาร และบริษัททัวร์ทั่วเอเชียตะวันออกเฉียงใต้ ลิซ่าเป็นผู้เชี่ยวชาญการตลาด Weibo และ WeChat ที่ได้รับการรับรอง และเป็นวิทยากรในงานประชุมอุตสาหกรรมเป็นประจำ',
      vi: 'Lisa lãnh đạo đội ngũ tiếp thị của chúng tôi với chuyên môn về các nền tảng mạng xã hội Trung Quốc bao gồm WeChat, Weibo, Xiaohongshu và Douyin. Cô đã quản lý các chiến dịch cho các thương hiệu xa xỉ, chuỗi F&B và công ty du lịch trên khắp Đông Nam Á. Lisa là chuyên gia tiếp thị Weibo và WeChat được chứng nhận và thường xuyên phát biểu tại các hội nghị ngành.'
    },
    member2Expertise1: {
      en: 'Social Media Marketing',
      ja: 'SNSマーケティング',
      th: 'การตลาดโซเชียลมีเดีย',
      vi: 'Tiếp thị mạng xã hội'
    },
    member2Expertise2: {
      en: 'Content Strategy',
      ja: 'コンテンツ戦略',
      th: 'กลยุทธ์เนื้อหา',
      vi: 'Chiến lược nội dung'
    },
    member2Expertise3: {
      en: 'Brand Management',
      ja: 'ブランド管理',
      th: 'การจัดการแบรนด์',
      vi: 'Quản lý thương hiệu'
    },
    member3Name: {
      en: 'Alex Tan',
      ja: 'アレックス・タン',
      th: 'อเล็กซ์ ตัน',
      vi: 'Alex Tan'
    },
    member3Position: {
      en: 'Chief Technology Officer',
      ja: 'チーフテクノロジーオフィサー',
      th: 'ประธานเจ้าหน้าที่ฝ่ายเทคโนโลยี',
      vi: 'Giám đốc Công nghệ'
    },
    member3ShortBio: {
      en: 'Technology and analytics specialist',
      ja: 'テクノロジーとアナリティクスのスペシャリスト',
      th: 'ผู้เชี่ยวชาญด้านเทคโนโลยีและการวิเคราะห์',
      vi: 'Chuyên gia công nghệ và phân tích'
    },
    member3FullBio: {
      en: 'Alex oversees all technical aspects of our campaigns, from marketing automation to data analytics. With a background in computer science and 10+ years in MarTech, he ensures our campaigns are optimized for maximum ROI. Alex is certified in Google Analytics, Facebook Business Manager, and various Chinese advertising platforms.',
      ja: 'アレックスは、マーケティングオートメーションからデータ分析まで、キャンペーンのすべての技術的側面を監督しています。コンピューターサイエンスのバックグラウンドとMarTechで10年以上の経験を持ち、キャンペーンが最大のROIを得るように最適化されることを保証します。Google Analytics、Facebook Business Manager、各種中国広告プラットフォームの認定を受けています。',
      th: 'อเล็กซ์ดูแลด้านเทคนิคทั้งหมดของแคมเปญของเรา ตั้งแต่การทำการตลาดอัตโนมัติไปจนถึงการวิเคราะห์ข้อมูล ด้วยพื้นฐานวิทยาการคอมพิวเตอร์และประสบการณ์กว่า 10 ปีใน MarTech เขาทำให้มั่นใจว่าแคมเปญของเราได้รับการปรับให้เหมาะสมเพื่อ ROI สูงสุด อเล็กซ์ได้รับการรับรองใน Google Analytics, Facebook Business Manager และแพลตฟอร์มโฆษณาจีนต่างๆ',
      vi: 'Alex giám sát tất cả các khía cạnh kỹ thuật của chiến dịch của chúng tôi, từ tự động hóa tiếp thị đến phân tích dữ liệu. Với nền tảng khoa học máy tính và hơn 10 năm trong MarTech, anh đảm bảo các chiến dịch của chúng tôi được tối ưu hóa để đạt ROI tối đa. Alex được chứng nhận về Google Analytics, Facebook Business Manager và các nền tảng quảng cáo Trung Quốc khác nhau.'
    },
    member3Expertise1: {
      en: 'Marketing Technology',
      ja: 'マーケティングテクノロジー',
      th: 'เทคโนโลยีการตลาด',
      vi: 'Công nghệ tiếp thị'
    },
    member3Expertise2: {
      en: 'Data Analytics',
      ja: 'データアナリティクス',
      th: 'การวิเคราะห์ข้อมูล',
      vi: 'Phân tích dữ liệu'
    },
    member3Expertise3: {
      en: 'Campaign Optimization',
      ja: 'キャンペーン最適化',
      th: 'การเพิ่มประสิทธิภาพแคมเปญ',
      vi: 'Tối ưu hóa chiến dịch'
    },
    member4Name: {
      en: 'Sarah Lim',
      ja: 'サラ・リム',
      th: 'ซาร่าห์ ลิม',
      vi: 'Sarah Lim'
    },
    member4Position: {
      en: 'Creative Director',
      ja: 'クリエイティブディレクター',
      th: 'ผู้อำนวยการฝ่ายสร้างสรรค์',
      vi: 'Giám đốc Sáng tạo'
    },
    member4ShortBio: {
      en: 'Award-winning creative with multicultural background',
      ja: '多文化背景を持つ受賞歴のあるクリエイティブ',
      th: 'นักสร้างสรรค์ที่ได้รับรางวัลพร้อมพื้นฐานหลากหลายวัฒนธรรม',
      vi: 'Nhà sáng tạo đoạt giải thưởng với nền tảng đa văn hóa'
    },
    member4FullBio: {
      en: 'Sarah leads our creative team in developing culturally-relevant content for Chinese audiences. With a background in graphic design and cultural studies, she ensures all our creative work resonates with target audiences while maintaining brand integrity. Sarah has won multiple awards for cross-cultural advertising campaigns.',
      ja: 'サラは、中国の視聴者向けに文化的に関連性のあるコンテンツを開発する創造的なチームを率いています。グラフィックデザインと文化研究のバックグラウンドを持ち、すべての創造的な作品がブランドの完全性を維持しながらターゲットオーディエンスに共鳴することを保証します。異文化広告キャンペーンで複数の賞を受賞しています。',
      th: 'ซาร่าห์นำทีมสร้างสรรค์ของเราในการพัฒนาเนื้อหาที่เกี่ยวข้องทางวัฒนธรรมสำหรับผู้ชมชาวจีน ด้วยพื้นฐานการออกแบบกราฟิกและการศึกษาวัฒนธรรม เธอทำให้มั่นใจว่างานสร้างสรรค์ทั้งหมดของเราสะท้อนกับกลุ่มเป้าหมายในขณะที่รักษาความสมบูรณ์ของแบรนด์ ซาร่าห์ได้รับรางวัลหลายรางวัลสำหรับแคมเปญโฆษณาข้ามวัฒนธรรม',
      vi: 'Sarah lãnh đạo đội ngũ sáng tạo của chúng tôi trong việc phát triển nội dung phù hợp với văn hóa cho khán giả Trung Quốc. Với nền tảng thiết kế đồ họa và nghiên cứu văn hóa, cô đảm bảo tất cả các tác phẩm sáng tạo của chúng tôi cộng hưởng với đối tượng mục tiêu trong khi duy trì tính toàn vẹn của thương hiệu. Sarah đã giành được nhiều giải thưởng cho các chiến dịch quảng cáo xuyên văn hóa.'
    },
    member4Expertise1: {
      en: 'Creative Strategy',
      ja: 'クリエイティブ戦略',
      th: 'กลยุทธ์สร้างสรรค์',
      vi: 'Chiến lược sáng tạo'
    },
    member4Expertise2: {
      en: 'Cultural Adaptation',
      ja: '文化適応',
      th: 'การปรับตัวทางวัฒนธรรม',
      vi: 'Thích ứng văn hóa'
    },
    member4Expertise3: {
      en: 'Visual Design',
      ja: 'ビジュアルデザイン',
      th: 'การออกแบบภาพ',
      vi: 'Thiết kế thị giác'
    },
    member5Name: {
      en: 'Michael Zhang',
      ja: 'マイケル・チャン',
      th: 'ไมเคิล จาง',
      vi: 'Michael Zhang'
    },
    member5Position: {
      en: 'Operations Manager',
      ja: 'オペレーションマネージャー',
      th: 'ผู้จัดการฝ่ายปฏิบัติการ',
      vi: 'Quản lý Vận hành'
    },
    member5ShortBio: {
      en: 'Operations expert ensuring seamless project delivery',
      ja: 'シームレスなプロジェクト提供を保証する運用エキスパート',
      th: 'ผู้เชี่ยวชาญด้านการดำเนินงานที่ทำให้มั่นใจในการส่งมอบโครงการอย่างราบรื่น',
      vi: 'Chuyên gia vận hành đảm bảo giao hàng dự án liền mạch'
    },
    member5FullBio: {
      en: 'Michael manages our day-to-day operations across Singapore and Malaysia offices. With experience in project management and team coordination, he ensures all campaigns are delivered on time and within budget. Michael is PMP certified and specializes in cross-cultural team management.',
      ja: 'マイケルは、シンガポールとマレーシアのオフィス全体の日常業務を管理しています。プロジェクト管理とチーム調整の経験を持ち、すべてのキャンペーンが時間通りかつ予算内で提供されることを保証します。PMP認定を受けており、異文化チーム管理を専門としています。',
      th: 'ไมเคิลจัดการการดำเนินงานประจำวันของเราในสำนักงานสิงคโปร์และมาเลเซีย ด้วยประสบการณ์ในการจัดการโครงการและการประสานงานทีม เขาทำให้มั่นใจว่าแคมเปญทั้งหมดถูกส่งมอบตรงเวลาและภายในงบประมาณ ไมเคิลได้รับการรับรอง PMP และเชี่ยวชาญในการจัดการทีมข้ามวัฒนธรรม',
      vi: 'Michael quản lý hoạt động hàng ngày của chúng tôi tại các văn phòng Singapore và Malaysia. Với kinh nghiệm trong quản lý dự án và phối hợp nhóm, anh đảm bảo tất cả các chiến dịch được giao đúng thời hạn và trong ngân sách. Michael có chứng chỉ PMP và chuyên về quản lý nhóm đa văn hóa.'
    },
    member5Expertise1: {
      en: 'Project Management',
      ja: 'プロジェクト管理',
      th: 'การจัดการโครงการ',
      vi: 'Quản lý dự án'
    },
    member5Expertise2: {
      en: 'Team Coordination',
      ja: 'チーム調整',
      th: 'การประสานงานทีม',
      vi: 'Phối hợp nhóm'
    },
    member5Expertise3: {
      en: 'Process Optimization',
      ja: 'プロセス最適化',
      th: 'การเพิ่มประสิทธิภาพกระบวนการ',
      vi: 'Tối ưu hóa quy trình'
    },
    member6Name: {
      en: 'Emily Wu',
      ja: 'エミリー・ウー',
      th: 'เอมิลี่ วู',
      vi: 'Emily Wu'
    },
    member6Position: {
      en: 'Content Strategy Lead',
      ja: 'コンテンツ戦略リード',
      th: 'หัวหน้ากลยุทธ์เนื้อหา',
      vi: 'Trưởng nhóm Chiến lược Nội dung'
    },
    member6ShortBio: {
      en: 'Bilingual content specialist and copywriter',
      ja: 'バイリンガルコンテンツスペシャリスト兼コピーライター',
      th: 'ผู้เชี่ยวชาญเนื้อหาสองภาษาและนักเขียนคำโฆษณา',
      vi: 'Chuyên gia nội dung song ngữ và người viết quảng cáo'
    },
    member6FullBio: {
      en: 'Emily specializes in creating compelling content that bridges cultures and languages. Fluent in English, Mandarin, and Cantonese, she crafts messages that resonate with diverse Chinese-speaking audiences across different regions. Emily has a journalism background and 8+ years in content marketing.',
      ja: 'エミリーは、文化と言語を橋渡しする魅力的なコンテンツの作成を専門としています。英語、北京語、広東語に堪能で、さまざまな地域の多様な中国語話者の聴衆に響くメッセージを作成します。ジャーナリズムのバックグラウンドとコンテンツマーケティングで8年以上の経験があります。',
      th: 'เอมิลี่เชี่ยวชาญในการสร้างเนื้อหาที่น่าสนใจที่เชื่อมโยงวัฒนธรรมและภาษา คล่องแคล่วในภาษาอังกฤษ จีนกลาง และกวางตุ้ง เธอสร้างข้อความที่สะท้อนกับผู้ชมที่พูดภาษาจีนที่หลากหลายในภูมิภาคต่างๆ เอมิลี่มีพื้นฐานด้านวารสารศาสตร์และประสบการณ์ในการตลาดเนื้อหามากกว่า 8 ปี',
      vi: 'Emily chuyên tạo nội dung hấp dẫn kết nối văn hóa và ngôn ngữ. Thông thạo tiếng Anh, tiếng Quan Thoại và tiếng Quảng Đông, cô tạo ra những thông điệp cộng hưởng với đa dạng khán giả nói tiếng Trung trên các khu vực khác nhau. Emily có nền tảng báo chí và hơn 8 năm trong tiếp thị nội dung.'
    },
    member6Expertise1: {
      en: 'Content Creation',
      ja: 'コンテンツ作成',
      th: 'การสร้างเนื้อหา',
      vi: 'Tạo nội dung'
    },
    member6Expertise2: {
      en: 'Copywriting',
      ja: 'コピーライティング',
      th: 'การเขียนคำโฆษณา',
      vi: 'Viết quảng cáo'
    },
    member6Expertise3: {
      en: 'Localization',
      ja: 'ローカライゼーション',
      th: 'การแปลเป็นภาษาท้องถิ่น',
      vi: 'Bản địa hóa'
    },
    member7Name: {
      en: 'James Liu',
      ja: 'ジェームズ・リウ',
      th: 'เจมส์ หลิว',
      vi: 'James Liu'
    },
    member7Position: {
      en: 'KOL Partnership Manager',
      ja: 'KOLパートナーシップマネージャー',
      th: 'ผู้จัดการความร่วมมือ KOL',
      vi: 'Quản lý Đối tác KOL'
    },
    member7ShortBio: {
      en: 'Influencer marketing specialist with extensive network',
      ja: '広範なネットワークを持つインフルエンサーマーケティングスペシャリスト',
      th: 'ผู้เชี่ยวชาญการตลาดอินฟลูเอนเซอร์พร้อมเครือข่ายที่กว้างขวาง',
      vi: 'Chuyên gia tiếp thị người ảnh hưởng với mạng lưới rộng lớn'
    },
    member7FullBio: {
      en: 'James manages our relationships with key opinion leaders and micro-influencers across China and Southeast Asia. With over 8 years in influencer marketing, he has built a network of 500+ verified KOLs across various industries. James is expert in campaign planning, content collaboration, and performance tracking.',
      ja: 'ジェームズは、中国と東南アジア全域の主要オピニオンリーダーとマイクロインフルエンサーとの関係を管理しています。インフルエンサーマーケティングで8年以上の経験を持ち、さまざまな業界で500人以上の検証済みKOLのネットワークを構築しています。キャンペーン計画、コンテンツコラボレーション、パフォーマンス追跡の専門家です。',
      th: 'เจมส์จัดการความสัมพันธ์ของเรากับผู้นำทางความคิดและไมโครอินฟลูเอนเซอร์ทั่วจีนและเอเชียตะวันออกเฉียงใต้ ด้วยประสบการณ์กว่า 8 ปีในการตลาดอินฟลูเอนเซอร์ เขาได้สร้างเครือข่ายของ KOL ที่ได้รับการยืนยันกว่า 500 คนในอุตสาหกรรมต่างๆ เจมส์เป็นผู้เชี่ยวชาญในการวางแผนแคมเปญ การร่วมมือด้านเนื้อหา และการติดตามประสิทธิภาพ',
      vi: 'James quản lý mối quan hệ của chúng tôi với các nhà lãnh đạo ý kiến chủ chốt và người ảnh hưởng nhỏ trên khắp Trung Quốc và Đông Nam Á. Với hơn 8 năm trong tiếp thị người ảnh hưởng, anh đã xây dựng mạng lưới hơn 500 KOL đã xác minh trong nhiều ngành khác nhau. James là chuyên gia về lập kế hoạch chiến dịch, hợp tác nội dung và theo dõi hiệu suất.'
    },
    member7Expertise1: {
      en: 'Influencer Relations',
      ja: 'インフルエンサーパートナーシップ',
      th: 'ความสัมพันธ์กับอินฟลูเอนเซอร์',
      vi: 'Quan hệ người ảnh hưởng'
    },
    member7Expertise2: {
      en: 'Campaign Management',
      ja: 'キャンペーン管理',
      th: 'การจัดการแคมเปญ',
      vi: 'Quản lý chiến dịch'
    },
    member7Expertise3: {
      en: 'Performance Analytics',
      ja: 'パフォーマンス分析',
      th: 'การวิเคราะห์ประสิทธิภาพ',
      vi: 'Phân tích hiệu suất'
    }
  },
  // Form and UI elements
  forms: {
    selectSubject: {
      en: 'Select a subject',
      ja: '件名を選択してください',
      th: 'เลือกหัวข้อ',
      vi: 'Chọn chủ đề'
    },
    geoSolutionInquiry: {
      en: 'GEO Solution Inquiry',
      ja: 'GEOソリューション',
      th: 'สอบถาม GEO Solution',
      vi: 'Yêu cầu về GEO Solution'
    },
    socialMediaInquiry: {
      en: 'SNS & O2O Marketing Solution',
      ja: 'SNS & O2Oマーケティングソリューション',
      th: 'โซลูชันการตลาด SNS & O2O',
      vi: 'Giải pháp Marketing SNS & O2O'
    },
    socialCommerceInquiry: {
      en: 'Social Commerce & EC Solution',
      ja: 'ソーシャルコマース & ECソリューション',
      th: 'โซลูชัน Social Commerce & EC',
      vi: 'Giải pháp Social Commerce & EC'
    },
    searchAdsInquiry: {
      en: 'Search Ads & Media Outreach',
      ja: '検索広告とメディアPR',
      th: 'โฆษณาการค้นหาและการเข้าถึงสื่อ',
      vi: 'Quảng cáo Tìm kiếm & Tiếp cận Truyền thông'
    },
    newsInsightsInquiry: {
      en: 'Market Insights',
      ja: '市場インサイト',
      th: 'ข่าวและข้อมูลเชิงลึกตลาด',
      vi: 'Thông tin Thị trường'
    },
    partnershipInquiry: {
      en: 'Partnership Opportunity',
      ja: 'パートナーシップに関するご相談',
      th: 'โอกาสความร่วมมือ',
      vi: 'Cơ hội Hợp tác'
    },
    generalInquiry: {
      en: 'General Inquiry',
      ja: '一般お問い合わせ',
      th: 'สอบถามทั่วไป',
      vi: 'Yêu cầu Chung'
    },
    formSubmissionMessage: {
      en: 'Form submission will be implemented soon!',
      ja: 'フォーム送信機能は近日中に公開予定です！',
      th: 'ฟังก์ชันส่งแบบฟอร์มจะถูกใช้งานเร็วๆ นี้!',
      vi: 'Chức năng gửi biểu mẫu sẽ được triển khai sớm!'
    },
    newsletterMessage: {
      en: 'Newsletter subscription will be implemented soon!',
      ja: 'ニュースレター購読機能はまもなく公開予定です！',
      th: 'ฟังก์ชันสมัครรับจดหมายข่าวจะถูกใช้งานเร็วๆ นี้!',
      vi: 'Chức năng đăng ký bản tin sẽ được triển khai sớm!'
    },
    ariaYourName: {
      en: 'Your name',
      ja: 'お名前',
      th: 'ชื่อของคุณ',
      vi: 'Tên của bạn'
    },
    ariaYourEmail: {
      en: 'Your email',
      ja: 'メールアドレス',
      th: 'อีเมลของคุณ',
      vi: 'Email của bạn'
    },
    ariaMessageSubject: {
      en: 'Message subject',
      ja: '件名',
      th: 'หัวข้อข้อความ',
      vi: 'Chủ đề tin nhắn'
    },
    ariaYourMessage: {
      en: 'Your message',
      ja: 'メッセージ',
      th: 'ข้อความของคุณ',
      vi: 'Tin nhắn của bạn'
    },
    ariaEmailNewsletter: {
      en: 'Email for newsletter',
      ja: 'メールアドレス',
      th: 'อีเมลสำหรับจดหมายข่าว',
      vi: 'Email cho bản tin'
    }
  },
  // Alt text and aria labels
  accessibility: {
    logoText: {
      en: 'Logo',
      ja: 'ロゴ',
      th: 'โลโก้',
      vi: 'Logo'
    },
    altChineseMarket: {
      en: 'Chinese Market Connection',
      ja: '中華圏市場との連携',
      th: 'การเชื่อมต่อตลาดจีน',
      vi: 'Kết nối Thị trường Trung Quốc'
    },
    altZCreative: {
      en: 'Z Creative Partners',
      ja: 'Z Creative Partners',
      th: 'Z Creative Partners',
      vi: 'Z Creative Partners'
    },
    ariaToggleMenu: {
      en: 'Toggle menu',
      ja: 'メニュー切り替え',
      th: 'สลับเมนู',
      vi: 'Chuyển đổi menu'
    },
    ariaChangeLanguage: {
      en: 'Change language',
      ja: '言語変更',
      th: 'เปลี่ยนภาษา',
      vi: 'Thay đổi ngôn ngữ'
    },
    altSocialMedia: {
      en: 'Chinese Social Media Marketing Platforms',
      ja: '中華圏SNSマーケティングプラットフォーム',
      th: 'แพลตฟอร์มการตลาดโซเชียลมีเดียจีน',
      vi: 'Nền tảng Tiếp thị Mạng xã hội Trung Quốc'
    },
    altBaiduSearch: {
      en: 'Baidu Search Ads & Media Outreach',
      ja: 'Baidu検索広告とメディアPR',
      th: 'โฆษณาการค้นหา Baidu และการเข้าถึงสื่อ',
      vi: 'Quảng cáo Tìm kiếm Baidu & Tiếp cận Truyền thông'
    },
    altMarketInsights: {
      en: 'Chinese Market Insights and Trends',
      ja: '市場のインサイトとトレンド',
      th: 'ข้อมูลเชิงลึกและแนวโน้มตลาดจีน',
      vi: 'Thông tin chi tiết và Xu hướng Thị trường Trung Quốc'
    },
    ariaHomepage: {
      en: 'Homepage',
      ja: 'ホームページ',
      th: 'หน้าแรก',
      vi: 'Trang chủ'
    },
    ariaInstagram: {
      en: 'Instagram',
      ja: 'Instagram',
      th: 'Instagram',
      vi: 'Instagram'
    },
    ariaFacebook: {
      en: 'Facebook',
      ja: 'Facebook',
      th: 'Facebook',
      vi: 'Facebook'
    },
    errorLoadingImage: {
      en: 'Error loading image',
      ja: '画像の読み込みエラー',
      th: 'เกิดข้อผิดพลาดในการโหลดรูปภาพ',
      vi: 'Lỗi tải hình ảnh'
    }
  },
  // Common translations used across multiple sections
  common: {
    background: {
      en: 'Background',
      ja: '背景',
      th: 'ภูมิหลัง',
      vi: 'Bối cảnh'
    },
    ourSolution: {
      en: 'Our Solution',
      ja: 'ソリューション',
      th: 'โซลูชันของเรา',
      vi: 'Giải pháp của chúng tôi'
    },
    impactHighlights: {
      en: 'Impact Highlights',
      ja: '成果・インパクト',
      th: 'ผลกระทบที่โดดเด่น',
      vi: 'Kết quả nổi bật'
    }
  },
  // Case Studies translations
  caseStudies: {
    fnb: {
      title: {
        en: 'Food & Beverage Case Studies',
        ja: '飲食業界のケーススタディ',
        th: '',
        vi: 'Case Study ngành Thực phẩm & Đồ uống'
      },
      tempuraYamaya: {
        background: {
          en: 'Tempura Yamaya is a well-known Japanese restaurant brand, recognized for its authentic tempura dishes and seasonal Japanese cuisine. The brand wanted to strengthen its appeal among Chinese residents and tourists in Thailand to drive more in-store traffic and increase awareness.',
          ja: '天ぷらやまやは、本格的な天ぷらと季節の和食で知られる有名な日本料理ブランド。タイ在住の中国人および中華圏観光客へのアピールを強化し、店舗への来客数と認知度の向上が目標。',
          th: '',
          vi: 'Tempura Yamaya là một thương hiệu nhà hàng Nhật Bản nổi tiếng, được biết đến với các món tempura truyền thống và ẩm thực Nhật Bản theo mùa.Thương hiệu mong muốn mở rộng sức hút đối với cư dân và khách du lịch Trung Quốc tại Thái Lan nhằm tăng lượng khách đến cửa hàng và nâng cao nhận diện thương hiệu.'
        },
        solution: {
          title: {
            en: 'To address this, we provided tailored support, including:',
            ja: 'この課題に対し、以下のカスタマイズされたサポートを提供：',
            th: '',
            vi: 'Để đáp ứng mục tiêu này, chúng tôi đã cung cấp giải pháp hỗ trợ toàn diện, bao gồm:'
          },
          point1: {
            en: 'Development and management of official Chinese social media channels',
            ja: '公式中国語SNSチャンネルの開発と運営',
            th: '',
            vi: 'Phát triển và quản lý các kênh mạng xã hội chính thức dành cho thị trường Trung Quốc.'
          },
          point2: {
            en: 'Collaboration with trusted Chinese KOLs/KOCs to drive authentic reviews and word-of-mouth',
            ja: '信頼できる中華圏KOL/KOCとのコラボレーションによる本格的なレビューと口コミの創出',
            th: '',
            vi: 'Hợp tác với các KOL/KOC Trung Quốc đáng tin cậy để lan tỏa đánh giá chân thực và tạo hiệu ứng truyền miệng.'
          },
          point3: {
            en: 'Chinese-friendly in-store marketing and promotional materials to enhance the dining experience',
            ja: '中華圏観光客向けの店内マーケティングおよび販促物による食事体験の向上',
            th: '',
            vi: 'Triển khai các hoạt động marketing tại cửa hàng và tài liệu quảng bá thân thiện với khách Trung Quốc nhằm nâng cao trải nghiệm dùng bữa.'
          }
        },
        impact: {
          point1: {
            en: 'Successfully expanded the brand\'s reach and engagement among Chinese customers.',
            ja: '中華圏顧客へのブランドリーチとエンゲージメントの拡大に成功',
            th: '',
            vi: 'Mở rộng thành công phạm vi tiếp cận và mức độ tương tác với khách hàng Trung Quốc.'
          },
          point2: {
            en: 'Not only increased traffic to the city branch but also boosted customer visits to other branches through strengthened brand image.',
            ja: '市内店舗への来客増だけでなく、ブランドイメージ強化により他店舗への来客も増加',
            th: '',
            vi: 'Mở rộng thành công phạm vi tiếp cận và mức độ tương tác với khách hàng Trung Quốc.'
          },
          point3: {
            en: 'Ongoing growth in followers, reach, and authentic user-generated content across Chinese social media platforms',
            ja: '中国語SNSプラットフォーム全体でのフォロワー、リーチ、ユーザー生成コンテンツの継続的な成長',
            th: '',
            vi: 'Gia tăng đều đặn lượng người theo dõi, mức độ tiếp cận và nội dung do người dùng tạo ra một cách chân thực trên các nền tảng mạng xã hội Trung Quốc.'
          }
        },
        snsTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        },
        backgroundTitle: {
          en: 'Background',
          ja: '背景',
          th: '',
          vi: 'Bối cảnh'
        },
        solutionTitle: {
          en: 'Our Solution',
          ja: 'ソリューション',
          th: '',
          vi: 'Giải pháp của chúng tôi'
        },
        impactTitle: {
          en: 'Impact Highlights',
          ja: '成果・インパクト',
          th: '',
          vi: 'Kết quả nổi bật'
        }
      },
      omakaseDon: {
        background: {
          en: 'OmakaseDon by Teppen is a renowned Japanese restaurant brand in Thailand. It aimed to become a must-visit destination for Chinese tourists and residents. To achieve this, the brand needed strategic KOL promotion and consistent Chinese-language communication to build trust and excitement.',
          ja: 'OmakaseDon by Teppenはタイで有名な日本料理レストランブランド。中華圏観光客および在住者にとって必訪の場所となることが目標。そのためには、戦略的なKOLプロモーションと一貫した中国語でのコミュニケーションによる信頼と期待の構築が必要。',
          th: '',
          vi: 'OmakaseDon by Teppen là một thương hiệu nhà hàng Nhật Bản nổi tiếng tại Thái Lan. Mục tiêu của họ là trở thành điểm đến không thể bỏ qua đối với khách Trung Quốc (cả du khách và người dân địa phương). Để đạt được điều đó, thương hiệu cần các chiến dịch KOL chiến lược và duy trì truyền thông bằng tiếng Trung một cách nhất quán nhằm xây dựng niềm tin và sự hào hứng.'
        },
        solution: {
          point1: {
            en: 'Strategic KOL campaigns featuring trusted Chinese influencers to generate authentic buzz and dining reviews',
            ja: '信頼できる中華圏インフルエンサーによる戦略的KOLキャンペーンで本格的な話題と食事レビューを生成',
            th: '',
            vi: 'Triển khai các chiến dịch hợp tác với các KOL Trung Quốc đáng tin cậy nhằm tạo độ phủ và tăng lượng đánh giá chân thực.'
          },
          point2: {
            en: 'Launch and management of the official Chinese social media account',
            ja: '公式中国語SNSアカウントの立ち上げと運営',
            th: '',
            vi: 'Khởi chạy và quản lý các kênh mạng xã hội chính thức bằng tiếng Trung.'
          },
          point3: {
            en: 'Engaging content to maintain high follower interaction and strengthen brand loyalty among Chinese customers',
            ja: '高いフォロワーエンゲージメントを維持し、中華圏顧客のブランドロイヤルティを強化する魅力的なコンテンツ',
            th: '',
            vi: 'Sản xuất nội dung hấp dẫn để duy trì lượng tương tác cao và tăng cường lòng trung thành của khách hàng Trung Quốc.'
          }
        },
        impact: {
          point1: {
            en: 'OmakaseDon by Teppen has become one of the most well-known restaurants among Chinese visitors in Thailand.',
            ja: 'OmakaseDon by Teppenはタイの中華圏訪問者の間で最も有名なレストランの1つに',
            th: '',
            vi: 'OmakaseDon by Teppen trở thành một trong những nhà hàng nổi tiếng nhất đối với khách Trung Quốc tại Thái Lan.'
          },
          point2: {
            en: 'High engagement achieved on each post, with strong likes, shares, and comments.',
            ja: '各投稿で高いエンゲージメントを達成、多くのいいね、シェア、コメントを獲得',
            th: '',
            vi: 'Mỗi bài đăng đều đạt mức độ tương tác cao với nhiều lượt thích, chia sẻ và bình luận.'
          },
          point3: {
            en: 'Continued growth in brand awareness and a steady flow of Chinese diners driven by online buzz and influencer recommendations.',
            ja: 'オンラインの話題とインフルエンサーの推薦によるブランド認知度の継続的な成長と安定した中華圏来客数',
            th: '',
            vi: 'Nhận diện thương hiệu liên tục tăng trưởng, với lượng khách hàng Trung Quốc ổn định nhờ vào hiệu ứng lan truyền trên mạng và các đề xuất từ KOL/KOC.'
          }
        },
        snsTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        },
        backgroundTitle: {
          en: 'Background',
          ja: '背景',
          th: '',
          vi: 'Bối cảnh'
        },
        solutionTitle: {
          en: 'Our Solution',
          ja: 'ソリューション',
          th: '',
          vi: 'Giải pháp của chúng tôi'
        },
        impactTitle: {
          en: 'Impact Highlights',
          ja: '成果・インパクト',
          th: '',
          vi: 'Kết quả nổi bật'
        }
      }
    },
    hotel: {
      title: {
        en: 'Hotels & Tourism Case Studies',
        ja: 'ホテル・観光業界のケーススタディ',
        th: '',
        vi: 'Case Study cho ngành Khách sạn & Du lịch'
      },
      richResidence: {
        background: {
          en: 'The Rich Residence had minimal exposure in the Chinese market—a key target segment for future growth. With no prior experience in Chinese social media or influencer promotion, the hotel faced challenges in building brand awareness, engaging Chinese travelers online, and providing a Chinese-friendly guest experience.',
          ja: 'The Rich Residenceは、将来の成長に向けた主要ターゲットセグメントである中華圏市場での露出が最小限。中華圏のSNSやインフルエンサープロモーションの経験がなく、ブランド認知度の構築、オンラインでの中華圏旅行者へのエンゲージメント、中華圏フレンドリーなゲスト体験の提供が課題。',
          th: '',
          vi: 'The Rich Residence gần như chưa có sự hiện diện nào trên thị trường Trung Quốc — một phân khúc khách hàng mục tiêu quan trọng cho sự tăng trưởng trong tương lai. Khách sạn này gặp khó khăn trong việc xây dựng thương hiệu, thu hút khách du lịch Trung Quốc trên nền tảng số, và mang lại trải nghiệm thân thiện với người Trung Quốc do chưa từng có kinh nghiệm về mạng xã hội Trung Quốc hay marketing KOL.'
        },
        solution1: {
          en: 'Chinese social media marketing strategy and execution',
          ja: '中華圏SNSマーケティング戦略と実行',
          th: '',
          vi: 'Chiến lược và triển khai marketing trên mạng xã hội Trung Quốc.'
        },
        solution2: {
          en: 'Official WeChat account management',
          ja: '公式WeChatアカウント管理',
          th: '',
          vi: 'Quản lý tài khoản chính thức WeChat.'
        },
        solution3: {
          en: 'KOL/KOC collaborations to generate authentic recommendations',
          ja: '本格的な推薦を生成するためのKOL/KOCコラボレーション',
          th: '',
          vi: 'Hợp tác với KOL/KOC để tạo ra các đề xuất chân thực.'
        },
        solution4: {
          en: 'On-site Chinese-friendly promotions and materials',
          ja: '現地での中国語利用者向けプロモーションと資料',
          th: '',
          vi: 'Triển khai các ấn phẩm và chương trình quảng bá thân thiện với người Trung Quốc ngay tại khách sạn.'
        },
        impact1: {
          en: '180% year-over-year increase in Chinese guest stay nights',
          ja: 'ホテルは現在、タイを訪れる中華圏旅行者の間で認知され、推奨されるオプションに',
          th: '',
          vi: 'Tăng 180% số đêm lưu trú của khách Trung Quốc so với cùng kỳ năm trước.'
        },
        impact2: {
          en: 'Achieved strong and steady growth in followers and reach on key Chinese platforms, helping to build brand awareness among Chinese travelers.',
          ja: 'KOLキャンペーンにより直接予約と問い合わせが大幅に増加',
          th: '',
          vi: 'Tăng trưởng ổn định về số lượng người theo dõi và độ phủ trên các nền tảng Trung Quốc chính.'
        },
        impact3: {
          en: 'Strengthened reputation as a Chinese-friendly serviced residence.',
          ja: '高品質のサービスと中華圏フレンドリーな体験により、リピート予約と紹介が増加',
          th: '',
          vi: 'Củng cố danh tiếng là một nơi lưu trú thân thiện với khách Trung Quốc.'
        },
        title: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      },
      shangriLa: {
        background: {
          en: 'Shangri-La Hotel, Chiang Mai is a 5-star urban resort known for its elegant Northern Thai style, lush gardens, and exceptional hospitality. While popular among international travelers, the hotel sought to strengthen its presence in the Chinese-speaking market and attract more high-value guests through social platforms.',
          ja: 'Shangri-La Hotel, Chiang Maiは、優雅な北タイスタイル、豊かな庭園、そして卓越したホスピタリティで知られる5つ星都市型リゾート。国際的な旅行者の間で人気がありながら、ホテルは中華圏での存在感を強化し、SNSプラットフォームを通じてより多くの高付加価値ゲストを引き付けることを目指していた。',
          th: '',
          vi: 'Khách sạn Shangri-La Chiang Mai là khu nghỉ dưỡng 5 sao giữa lòng thành phố, nổi tiếng với phong cách Bắc Thái sang trọng với khu vườn xanh mát và dịch vụ hiếu khách tuyệt vời. Dù đã rất được yêu thích bởi du khách quốc tế, khách sạn mong muốn tăng độ nhận diện trong thị trường khách nói tiếng Trung và thu hút nhiều du khách cao cấp hơn thông qua các nền tảng mạng xã hội..'
        },
        solution1: {
          en: 'Managed the official RedBook and WeChat account with high-quality posts.',
          ja: 'RedBookとWeChatの公式アカウントを高品質な投稿で運営。',
          th: '',
          vi: 'Quản lý tài khoản chính thức RedBook và WeChat với các bài đăng chất lượng cao.'
        },
        solution2: {
          en: 'KOL collaborations to generate authentic content and word-of-mouth.',
          ja: '本格的なコンテンツと口コミを生成するためのKOLコラボレーション。',
          th: '',
          vi: 'Hợp tác với KOL để tạo nội dung chân thực và lan tỏa truyền miệng.'
        },
        solution3: {
          en: 'Boosted post reach through targeted ad promotions to maximize visibility among Chinese-speaking travelers',
          ja: '中華圏の旅行者の間での可視性を最大化するために、ターゲット広告プロモーションを通じて投稿のリーチを向上。',
          th: '',
          vi: 'Tăng cường phạm vi tiếp cận bài đăng thông qua các chương trình quảng cáo có mục tiêu nhằm tối đa hóa khả năng hiển thị đối với khách du lịch nói tiếng Trung.'
        },
        impact1: {
          en: 'Solid growth in RedBook followers and engagement, boosting awareness among Chinese-speaking travelers.',
          ja: 'RedBook(Xiaohongshu)のフォロワーとエンゲージメントの着実な成長により、中華圏旅行者の間での認知度が向上。',
          th: '',
          vi: 'Tăng trưởng vững chắc về lượng người theo dõi và tương tác trên RedBook, nâng cao nhận thức trong cộng đồng khách du lịch nói tiếng Trung.'
        },
        impact2: {
          en: 'KOL posts achieved strong reach and interaction, leading to increased booking inquiries.',
          ja: 'KOLの投稿は強力なリーチとインタラクションを達成し、予約問い合わせの増加につながる。',
          th: '',
          vi: 'Các bài đăng KOL đạt mức độ lan tỏa và tương tác cao, dẫn đến số lượng yêu cầu đặt phòng tăng đáng kể.'
        },
        impact3: {
          en: 'Enhanced brand positioning as a top luxury-family hotel choice in Chiang Mai.',
          ja: 'チェンマイにおけるトップのラグジュアリー・ファミリー層向けのホテルとしての位置付けを強化。',
          th: '',
          vi: 'Củng cố vị thế thương hiệu như một trong những lựa chọn khách sạn sang trọng hàng đầu cho gia đình tại Chiang Mai.'
        },
        title: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      }
    },
    retail: {
      title: {
        en: 'Retail Case Studies',
        ja: '小売業界のケーススタディ',
        th: '',
        vi: 'Case Study cho ngành Bán lẻ'
      },
      siamTakashimaya: {
        background: {
          en: 'Siam Takashimaya, a premium Japanese department store in ICONSIAM Bangkok, is renowned for its high-quality Japanese brands and exceptional shopping experience. While well-established locally, the store aimed to attract more Chinese tourists and residents to increase foot traffic and sales.',
          ja: 'ICONSIAMバンコクにあるプレミアム日本デパート、サイアム高島屋は、高品質の日本ブランドと優れたショッピング体験で有名。地元では定評があるが、より多くの中華圏観光客と在住者を引き付けて来店数と売上の増加が目標。',
          th: '',
          vi: 'Siam Takashimaya, trung tâm thương mại Nhật Bản cao cấp tại ICONSIAM Bangkok, nổi tiếng với các thương hiệu Nhật chất lượng và trải nghiệm mua sắm xuất sắc. Tuy đã có chỗ đứng tại thị trường địa phương, thương hiệu muốn thu hút thêm khách du lịch và cư dân Trung Quốc để tăng lượng khách và doanh thu.'
        },
        solution: {
          title: {
            en: 'We provided comprehensive solutions to enhance their Chinese market presence:',
            ja: '中華圏市場での存在感を高めるための包括的なソリューションを提供：',
            th: '',
            vi: 'Chúng tôi đã cung cấp các giải pháp toàn diện để tăng cường sự hiện diện tại thị trường Trung Quốc:'
          },
          point1: {
            en: 'Multi-platform social media marketing, managing both RedBook(Xiaohongshu) and WeChat official accounts to communicate directly',
            ja: '中国語SNSでのブランドストーリーテリングと製品プロモーション',
            th: '',
            vi: 'Marketing mạng xã hội đa nền tảng, quản lý tài khoản chính thức trên RedBook (Xiaohongshu) và WeChat để giao tiếp trực tiếp.'
          },
          point2: {
            en: 'High-quality content production showcasing Japanese brands, seasonal promotions, and in-store highlights',
            ja: 'タイ在住の中華圏インフルエンサーとのKOL/KOCパートナーシップ',
            th: '',
            vi: 'Sản xuất nội dung chất lượng giới thiệu các thương hiệu Nhật Bản, chương trình khuyến mãi theo mùa và điểm nhấn mua sắm tại cửa hàng.'
          },
          point3: {
            en: 'O2O initiatives, including exclusive digital coupons, special shopping privileges, etc.',
            ja: 'WeChat限定プロモーションと特典によるエンゲージメントと購入の促進',
            th: '',
            vi: 'Triển khai chiến dịch O2O như phát hành mã giảm giá, đặc quyền mua sắm đặc biệt, v.v.'
          }
        },
        impact: {
          point1: {
            en: 'Significant growth in reach and engagement on both RedBook(Xiaohongshu) and WeChat.',
            ja: '中華圏観光客および在タイ駐在員の間で信頼される日本のショッピングデスティネーションとしてのブランドポジショニング強化',
            th: '',
            vi: 'Tăng trưởng rõ rệt về độ phủ và tương tác trên cả RedBook (Xiaohongshu) và WeChat.'
          },
          point2: {
            en: 'Stronger brand positioning as a trusted Japanese shopping destination among Chinese tourists and expat shoppers in Bangkok.',
            ja: 'クーポンと特典キャンペーンの適切な実行による店舗への来客増と売上コンバージョンの向上',
            th: '',
            vi: 'Định vị thương hiệu mạnh mẽ hơn, trở thành điểm mua sắm Nhật Bản đáng tin cậy với du khách và cư dân Trung Quốc tại Bangkok.'
          },
          point3: {
            en: 'Increased in-store traffic and higher sales conversion through well-executed coupon and privilege campaigns.',
            ja: '店舗への来客増と売上コンバージョンの向上',
            th: '',
            vi: 'Tăng lưu lượng khách trực tiếp và tỷ lệ chuyển đổi nhờ các chiến dịch khuyến mãi và đặc quyền được triển khai hiệu quả.'
          }
        },
        snsTitle: {
          en: 'Official Account Management in Social Media WeChat',
          ja: 'SNS WeChatでの公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên WeChat'
        },
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        },
        backgroundTitle: {
          en: 'Background',
          ja: '背景',
          th: '',
          vi: 'Bối cảnh'
        },
        solutionTitle: {
          en: 'Our Solution',
          ja: 'ソリューション',
          th: '',
          vi: 'Giải pháp của chúng tôi'
        },
        impactTitle: {
          en: 'Impact Highlights',
          ja: '成果・インパクト',
          th: '',
          vi: 'Kết quả nổi bật'
        }
      },
      case2: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      }
    },
    beautyAndSkincare: {
      title: {
        en: 'Beauty & Skincare Case Studies',
        ja: '美容・スキンケア業界のケーススタディ',
        th: '',
        vi: 'Case Study cho ngành Làm đẹp & Chăm sóc da'
      },
      case1: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      },
      case2: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        },
        title: {
          en: 'Official Account Management in Social Media WeChat',
          ja: 'SNS WeChatでの公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng WeChat'
        }
      },
      case3: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      }
    },
    fashion: {
      title: {
        en: 'Fashion Case Studies',
        ja: 'ファッション業界のケーススタディ',
        th: '',
        vi: 'Case Study cho ngành Thời trang'
      },
      case1: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      }
    },
    spaWellness: {
      title: {
        en: 'Spa & Wellness Case Studies',
        ja: 'スパ・ウェルネス業界のケーススタディ',
        th: '',
        vi: 'Case Study cho ngành Spa & Sức khoẻ'
      },
      case1: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      },
      case2: {
        title: {
          en: 'Official Account Management in Social Media RedBook(Xiaohongshu)',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Quản lý tài khoản chính thức trên nền tảng RedBook (Xiaohongshu)'
        }
      }
    },
    kolKoc: {
      title: {
        en: 'KOL/KOC Marketing Case Studies',
        ja: 'KOL/KOCマーケティングケーススタディ',
        th: '',
        vi: 'KOL/KOC Marketing Case Studies'
      },
      case1: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media Rednote',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Official Account Management in Social Media Rednote'
        }
      },
      case2: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media Rednote',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Official Account Management in Social Media Rednote'
        }
      },
      case3: {
        snsRedNoteTitle: {
          en: 'Official Account Management in Social Media Rednote',
          ja: 'SNS RedBook(Xiaohongshu)での公式アカウント運営',
          th: '',
          vi: 'Official Account Management in Social Media Rednote'
        }
      }
    }
  }
};

// Helper function to get nested translation
function getNestedTranslation(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Language context
interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language provider component
export function LanguageProvider({ children }: { children: ReactNode }) {
  // LANGUAGE SWITCHING CONTROL - Modified to allow English, Japanese, and Vietnamese
  // English, Japanese, and Vietnamese enabled, Thai disabled (Updated - 2025-09-02)
  const ALLOWED_LANGUAGES: LanguageCode[] = ['en', 'ja', 'vi']; // English, Japanese, and Vietnamese allowed
  
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>('en');

  const setLanguage = useCallback((language: LanguageCode) => {
    if (!ALLOWED_LANGUAGES.includes(language)) {
      // Only allow English, Japanese, and Vietnamese, block Thai
      console.log(`Language ${language} is currently disabled. Only English, Japanese, and Vietnamese are available.`);
      return;
    }
    setCurrentLanguage(language);
    // Persist language selection
    try {
      localStorage.setItem('selectedLanguage', language);
      localStorage.setItem('userSelectedLanguage', 'true'); // Mark that user has manually selected
    } catch (error) {
      // Handle localStorage errors gracefully
      console.warn('Could not save language to localStorage:', error);
    }
  }, []);

  // Memoized translation function with fallback
  const t = useCallback((key: string): string => {
    const translation = getNestedTranslation(translations, key);
    if (translation && translation.hasOwnProperty(currentLanguage)) {
      return translation[currentLanguage];
    }
    // Fallback to English if translation doesn't exist
    if (translation && translation.hasOwnProperty('en')) {
      return translation.en;
    }
    // Return key if no translation found (for debugging)
    return key;
  }, [currentLanguage]);

  // Modified: Allow Japanese auto-detection but block Thai and Vietnamese (Update #10 modified - 2025-08-28)
  // Load saved language or auto-detect on mount
  useEffect(() => {
    // Check if saved language is allowed
    const savedLang = localStorage.getItem('selectedLanguage') as LanguageCode;
    if (savedLang && !ALLOWED_LANGUAGES.includes(savedLang)) {
      // If saved language is not allowed, default to English
      setCurrentLanguage('en');
      localStorage.setItem('selectedLanguage', 'en');
      return;
    }
    
    try {
      const savedLanguage = localStorage.getItem('selectedLanguage') as LanguageCode;
      const userSelected = localStorage.getItem('userSelectedLanguage');
      
      if (savedLanguage && languages[savedLanguage]) {
        setCurrentLanguage(savedLanguage);
      } else if (!userSelected) {
        // Auto-detect language from browser if user hasn't manually selected
        const browserLang = navigator.language.toLowerCase();
        
        // Only auto-detect Japanese, ignore Thai and Vietnamese
        if (browserLang.startsWith('ja') && ALLOWED_LANGUAGES.includes('ja')) {
          setCurrentLanguage('ja');
          localStorage.setItem('selectedLanguage', 'ja');
        } else {
          // Default to English for all other languages (including Thai and Vietnamese)
          setCurrentLanguage('en');
          localStorage.setItem('selectedLanguage', 'en');
        }
      }
    } catch (error) {
      // Handle localStorage errors gracefully
      console.warn('Could not load language from localStorage:', error);
    }
  }, [ALLOWED_LANGUAGES]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentLanguage,
    setLanguage,
    t
  }), [currentLanguage, setLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}